import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { SEO } from '../../components/common/SEO';
import {
  Award,
  Users,
  Utensils,
  Armchair,
  Sparkles,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Send,
  Copy,
  Phone,
  MessageCircle,
  ShieldCheck,
  ArrowRight,
  HelpCircle,
  Check,
  Building,
  Calendar,
  MapPin,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  UserCheck,
  QrCode,
  FileCheck,
  Lock,
  LogIn,
  Search,
  ChevronDown,
  UserPlus,
  Coffee,
  Upload,
  FileText,
  Loader2,
  ScanText,
  Info,
  RefreshCw
} from 'lucide-react';
import { Gelanggang, UserProfile, InvoiceItem, PersatuanPaymentSettings, GelanggangDetail } from '../../types';
import { formatPublicName, isChildOfParent, isFamilyMember, formatEventDates, parseReceiptTimestamp } from '../../utils/formatters';
import {
  getMembersDB,
  saveInvoiceDB,
  saveTransactionDB,
  getUjianBengkungFeeForLevel,
  getNoticesDB,
  getUjianBengkungFeesDB,
  UjianBengkungFeeConfig,
  getPersatuanPaymentSettingsDB,
  DEFAULT_PERSATUAN_PAYMENT_SETTINGS,
  getGelanggangDB
} from '../../services/db';
import { uploadYuranReceiptToAhliDriveJson } from '../../lib/gdrive';
import { scanReceiptFile } from '../../utils/receiptScanner';
import {
  getNextBengkungRank,
  cleanBengkungTitle,
  toDbBengkungCode,
  ASMM_BENGKUNG_RANKS,
  BengkungRankInfo
} from '../../data/bengkungRanks';

interface UjianBengkungPageProps {
  onNavigateToTab?: (tab: string, fullUrl?: string) => void;
}

interface FamilyCandidate {
  id: string;
  name: string;
  icNo: string;
  memberId?: string;
  currentBengkung?: string;
  targetBengkung: string;
  examFee: number;
}

/**
 * Resolves the rank index of a given bengkung string in ASMM_BENGKUNG_RANKS.
 * Returns -1 if empty, unrecognized, or pesilat baharu.
 */
export function getCandidateRankIndex(bengkung?: string): number {
  if (!bengkung) return -1;
  const raw = bengkung.trim().toLowerCase();
  if (raw === '' || raw === 'tiada' || raw.includes('baharu') || raw.includes('baru')) return -1;

  const dbCode = toDbBengkungCode(bengkung);
  const clean = cleanBengkungTitle(bengkung).toLowerCase().trim();

  let idx = ASMM_BENGKUNG_RANKS.findIndex((r) => r.dbCode === dbCode);
  if (idx !== -1) return idx;

  idx = ASMM_BENGKUNG_RANKS.findIndex(
    (r) =>
      r.code.toLowerCase() === clean ||
      r.bengkungName.toLowerCase() === clean ||
      clean.startsWith(r.code.toLowerCase())
  );
  return idx;
}

/**
 * Returns available target ranks for Ujian Bengkung.
 * Strictly excludes current rank and all lower ranks ("he will only upgrade and not retain").
 */
export function getAvailableTargetRanks(currentBengkung?: string): BengkungRankInfo[] {
  const currentIdx = getCandidateRankIndex(currentBengkung);
  return ASMM_BENGKUNG_RANKS.filter((r, idx) => {
    // Guru Utama is leadership title, not for examination
    if (r.code === 'Guru Utama') return false;
    // If pesilat has current bengkung, only load ranks strictly higher (idx > currentIdx)
    if (currentIdx >= 0) {
      return idx > currentIdx;
    }
    // If pesilat baharu (no bengkung), all ranks are available
    return true;
  });
}

/**
 * Formats a rank with its specific cula count and title for clear, detailed UI selection.
 * e.g. "Bengkung Hitam (Cula 1) - Tunas Pendekar Muda"
 */
export function formatBengkungDetailedWithCula(r: BengkungRankInfo): string {
  let culaDesc = '';
  if (r.module.includes('Kuning')) {
    culaDesc = r.culaCount === 0 ? 'Tanpa Cula' : `Cula ${r.culaCount} / Darjah ${r.culaCount}`;
  } else if (r.module.includes('Biru')) {
    culaDesc = 'Tanpa Cula';
  } else {
    culaDesc = r.culaCount === 0 ? 'Tanpa Cula' : `Cula ${r.culaCount}`;
  }

  const baseName = r.bengkungName.replace(/\s*\(Peringkat\s*\d+\)/i, '').replace(/\s*\(Darjah\s*\d+\)/i, '').trim();
  return `${baseName} (${culaDesc}) - ${r.rankTitle}`;
}

interface PaymentConfig {
  paymentMethod: 'fpx' | 'qr' | 'both';
  qrBankName: string;
  qrAccountName: string;
  qrAccountNo: string;
  qrNotes: string;
}

interface PricingConfig {
  freeMealsPerPax: number;
  mealPrice: number;
}

const DEFAULT_PRICING: PricingConfig = {
  freeMealsPerPax: 2,
  mealPrice: 15
};

export const UjianBengkungPage: React.FC<UjianBengkungPageProps> = ({ onNavigateToTab }) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  // Dynamic pricing configuration (customizable from Dashboard Notices page)
  const [pricingConfig, setPricingConfig] = useState<PricingConfig>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('asmm_ujian_pricing_config');
      if (saved) {
        try {
          const p = JSON.parse(saved);
          return {
            freeMealsPerPax: Number(p.freeMealsPerPax) || DEFAULT_PRICING.freeMealsPerPax,
            mealPrice: Number(p.mealOriginalPrice) || Number(p.mealPrice) || DEFAULT_PRICING.mealPrice
          };
        } catch { }
      }
    }
    return DEFAULT_PRICING;
  });

  // Event details configuration (customizable from Dashboard Notices page)
  const [eventMeta, setEventMeta] = useState<{
    eventDate: string;
    eventEndDate: string;
    eventTime: string;
    eventLocation: string;
    registrationDeadline: string;
  }>({
    eventDate: '2026-12-11',
    eventEndDate: '2026-12-12',
    eventTime: '8.00 Pagi - 2.00 Petang',
    eventLocation: 'Pusat Riadah Wadi AnNour',
    registrationDeadline: '2026-12-10'
  });

  // Sync pricing config and event details from DB notice or localStorage, and listen to live updates
  useEffect(() => {
    const syncPricing = async () => {
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('asmm_ujian_pricing_config');
        if (saved) {
          try {
            const p = JSON.parse(saved);
            setPricingConfig(prev => ({
              ...prev,
              freeMealsPerPax: Number(p.freeMealsPerPax) || prev.freeMealsPerPax,
              mealPrice: Number(p.mealOriginalPrice) || Number(p.mealPrice) || prev.mealPrice
            }));

            if (p.eventDate || p.eventEndDate || p.eventLocation || p.eventTime || p.registrationDeadline) {
              setEventMeta(prev => ({
                eventDate: p.eventDate || prev.eventDate,
                eventEndDate: p.eventEndDate !== undefined ? p.eventEndDate : prev.eventEndDate,
                eventTime: p.eventTime || prev.eventTime,
                eventLocation: p.eventLocation || prev.eventLocation,
                registrationDeadline: p.registrationDeadline || prev.registrationDeadline
              }));
            }
          } catch { }
        }
      }

      try {
        const notices = await getNoticesDB();
        const ujianNotice = notices.find(n => n.hasFamilyAddon || n.title.toLowerCase().includes('ujian') || n.title.toLowerCase().includes('bengkung'));
        if (ujianNotice) {
          if (ujianNotice.hasFamilyAddon) {
            setPricingConfig({
              freeMealsPerPax: ujianNotice.freeMealsPerPax ?? DEFAULT_PRICING.freeMealsPerPax,
              mealPrice: ujianNotice.mealOriginalPrice ?? DEFAULT_PRICING.mealPrice
            });
          }

          setEventMeta(prev => ({
            eventDate: ujianNotice.eventDate || prev.eventDate,
            eventEndDate: ujianNotice.eventEndDate !== undefined ? ujianNotice.eventEndDate : prev.eventEndDate,
            eventTime: ujianNotice.eventTime || prev.eventTime,
            eventLocation: ujianNotice.eventLocation || prev.eventLocation,
            registrationDeadline: ujianNotice.registrationDeadline || prev.registrationDeadline
          }));
        }
      } catch { }
    };

    syncPricing();
    window.addEventListener('asmm_db_updated', syncPricing);
    return () => window.removeEventListener('asmm_db_updated', syncPricing);
  }, []);

  // Gelanggang List loaded from DB (aaPanel / localStorage)
  const [gelanggangList, setGelanggangList] = useState<GelanggangDetail[]>([]);

  useEffect(() => {
    let isMounted = true;
    const loadGelanggang = async () => {
      try {
        const data = await getGelanggangDB();
        if (isMounted && data && data.length > 0) {
          setGelanggangList(data);
        }
      } catch (err) {
        console.warn('Gagal memuat senarai gelanggang:', err);
      }
    };
    loadGelanggang();
    window.addEventListener('asmm_db_updated', loadGelanggang);
    return () => {
      isMounted = false;
      window.removeEventListener('asmm_db_updated', loadGelanggang);
    };
  }, []);

  // Auto-fetch details of banking and DuitNow QR from Tetapan Persatuan (/settings/payment)
  const [persatuanSettings, setPersatuanSettings] = useState<PersatuanPaymentSettings>(() => {
    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem('asmm_persatuan_payment_settings');
        if (raw) {
          return {
            ...DEFAULT_PERSATUAN_PAYMENT_SETTINGS,
            ...JSON.parse(raw)
          };
        }
      } catch { }
    }
    return DEFAULT_PERSATUAN_PAYMENT_SETTINGS;
  });

  // Sync with aaPanel & localStorage whenever /settings/payment changes
  useEffect(() => {
    let isMounted = true;
    const syncPersatuanPayment = async () => {
      try {
        const settings = await getPersatuanPaymentSettingsDB();
        if (isMounted && settings) {
          setPersatuanSettings(settings);
        }
      } catch (err) {
        console.warn('Gagal memuat turun tetapan akaun bank persatuan:', err);
      }
    };

    syncPersatuanPayment();
    window.addEventListener('asmm_db_updated', syncPersatuanPayment);
    window.addEventListener('storage', syncPersatuanPayment);
    return () => {
      isMounted = false;
      window.removeEventListener('asmm_db_updated', syncPersatuanPayment);
      window.removeEventListener('storage', syncPersatuanPayment);
    };
  }, []);

  // Determine available payment methods from Persatuan settings
  const isFpxEnabled = persatuanSettings.fpxEnabled;
  const isQrEnabled = persatuanSettings.qrEnabled;

  // General Family & Guardian Info
  const [parentName, setParentName] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [gelanggang, setGelanggang] = useState<Gelanggang>('Pusaka Warisan');
  const [specialNotes, setSpecialNotes] = useState('');

  // Active Gelanggang details & payment configurations
  const activeGelanggangDetail = useMemo(() => {
    const match = gelanggangList.find(g => g.name === gelanggang);
    if (match) return match;

    if (gelanggang === 'Wadi AnNour') {
      return {
        id: 'gel-wadi',
        name: 'Wadi AnNour' as Gelanggang,
        location: 'Pusat Riadah Wadi AnNour, Kampung Simpang Tiga Keladi, 09000 Kulim, Kedah',
        sessionTime: 'Jumaat (3.30 PM - 6.30 PM)',
        leaderName: 'Mohd Fauzi Bin Abd Aziz',
        leaderPhone: '0124475157',
        status: 'Aktif' as const,
        paymentPreference: 'qr_ketua' as const,
        qrBankName: 'Maybank',
        qrAccountName: 'Mohd Fauzi Bin Abd Aziz',
        qrAccountNo: '152116105570'
      };
    }
    if (gelanggang === 'Paya Besar' || gelanggang === 'Gelanggang Paya Besar') {
      return {
        id: 'gel-paya',
        name: 'Paya Besar' as Gelanggang,
        location: 'SK Paya Besar, Lunas, 09000 Kulim, Kedah',
        sessionTime: 'Rabu & Sabtu (9.00 PM - 11.00 PM)',
        leaderName: 'Mohamad Shakir Fahmi Bin Mohamad Zaki',
        leaderPhone: '01128194012',
        status: 'Aktif' as const,
        paymentPreference: 'qr_ketua' as const,
        qrBankName: 'Maybank',
        qrAccountName: 'Mohamad Shakir Fahmi Bin Mohamad Zaki',
        qrAccountNo: '152050320021'
      };
    }
    // Default fallback: Pusaka Warisan
    return {
      id: 'gel-pusaka',
      name: 'Pusaka Warisan' as Gelanggang,
      location: 'Padang Awam Taman Sejahtera, 09600 Lunas, Kedah',
      sessionTime: 'Jumaat (8.00 AM - 11.00 AM) & Sabtu (8.00 PM - 10.00 PM)',
      leaderName: 'Fikriz Zaki Syeikh Ahmad Amirruddin',
      leaderPhone: '0135819201',
      status: 'Aktif' as const,
      paymentPreference: 'qr_ketua' as const,
      qrBankName: 'Maybank',
      qrAccountName: 'Mohammad Hafiz Bin Halim',
      qrAccountNo: '152050320021'
    };
  }, [gelanggangList, gelanggang]);

  // Derived payment fields for selected gelanggang
  const targetBankName = activeGelanggangDetail?.qrBankName || 'Maybank';
  const targetAccountHolder = activeGelanggangDetail?.qrAccountName || activeGelanggangDetail?.leaderName || `Pengurusan Gelanggang ${gelanggang}`;
  const targetAccountNo = activeGelanggangDetail?.qrAccountNo || '152050320021';
  const targetQrImage = activeGelanggangDetail?.qrImageUrl || '';
  const targetLeaderName = activeGelanggangDetail?.leaderName || `Ketua Gelanggang ${gelanggang}`;
  const targetLeaderPhone = activeGelanggangDetail?.leaderPhone || '0135819201';

  const cleanLeaderPhone = useMemo(() => {
    const raw = targetLeaderPhone.replace(/\D/g, '');
    if (raw.startsWith('60')) return raw;
    if (raw.startsWith('0')) return `60${raw.slice(1)}`;
    return `60${raw}`;
  }, [targetLeaderPhone]);

  const availableGelanggangOptions = useMemo(() => {
    const list = gelanggangList.filter(g => g.name !== 'Semua Gelanggang' && g.name !== 'Tiada');
    if (list.length > 0) return list;
    return [
      { id: 'gel-pusaka', name: 'Pusaka Warisan' as Gelanggang, location: 'Padang Awam Taman Sejahtera, Lunas' },
      { id: 'gel-wadi', name: 'Wadi AnNour' as Gelanggang, location: 'Pusat Riadah Wadi AnNour, Kulim' },
      { id: 'gel-paya', name: 'Paya Besar' as Gelanggang, location: 'SK Paya Besar, Kulim' }
    ];
  }, [gelanggangList]);

  // Active payment tab mode (defaults to 'qr')
  const [selectedPayMode, setSelectedPayMode] = useState<'fpx' | 'qr'>('qr');

  useEffect(() => {
    if (activeGelanggangDetail?.paymentPreference === 'fpx' && isFpxEnabled) {
      setSelectedPayMode('fpx');
    } else {
      setSelectedPayMode('qr');
    }
  }, [activeGelanggangDetail?.paymentPreference, isFpxEnabled]);

  // Dynamic Ujian Bengkung base fees fetched from DB (e.g. settings / ujian_bengkung_fees)
  const [ujianFeesConfig, setUjianFeesConfig] = useState<UjianBengkungFeeConfig | null>(null);

  useEffect(() => {
    const fetchFees = async () => {
      try {
        const fees = await getUjianBengkungFeesDB();
        setUjianFeesConfig(fees);
      } catch (err) {
        console.warn('Error fetching ujian bengkung fees from DB:', err);
      }
    };
    fetchFees();
    window.addEventListener('asmm_db_updated', fetchFees);
    return () => window.removeEventListener('asmm_db_updated', fetchFees);
  }, []);

  // Candidates for the whole family
  const [candidates, setCandidates] = useState<FamilyCandidate[]>([]);

  // Family Add-on quantities (Extra meals only, no selling of seats)
  const [extraMeals, setExtraMeals] = useState<number>(0);

  // Status states
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isAccountCopied, setIsAccountCopied] = useState(false);

  // User's linked family members
  const [familyMembers, setFamilyMembers] = useState<UserProfile[]>([]);
  const [availableChildren, setAvailableChildren] = useState<UserProfile[]>([]);
  const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(null);

  useEffect(() => {
    if (currentUser) {
      setParentName(currentUser.kinName || currentUser.name || '');
      setParentPhone(currentUser.kinPhone || currentUser.phone || '');
      if (currentUser.gelanggang) setGelanggang(currentUser.gelanggang);

      getMembersDB().then((allMembers) => {
        const fam = allMembers.filter((m) => {
          if (m.id === currentUser.id) return true;
          if (isFamilyMember(m, currentUser)) return true;
          return false;
        });

        setFamilyMembers(fam);
        const kids = fam.filter((m) => isChildOfParent(m, currentUser) && m.status === 'Aktif');
        setAvailableChildren(kids);

        setCandidates((prev) => {
          if (prev.length > 0) return prev;

          if (kids.length > 0) {
            return kids.map((k, idx) => {
              const cur = k.bengkung ? cleanBengkungTitle(k.bengkung) : '';
              const available = getAvailableTargetRanks(cur);
              const target = available.length > 0 ? available[0].code : (k.bengkung ? getNextBengkungRank(k.bengkung) : 'Hitam 1');
              const fee = getUjianBengkungFeeForLevel(target, ujianFeesConfig || undefined);
              return {
                id: `cand-${idx + 1}-${k.id}`,
                name: k.name,
                icNo: k.icNo || '',
                memberId: k.id,
                currentBengkung: cur,
                targetBengkung: target,
                examFee: fee
              };
            });
          } else {
            const cur = currentUser.bengkung ? cleanBengkungTitle(currentUser.bengkung) : '';
            const available = getAvailableTargetRanks(cur);
            const target = available.length > 0 ? available[0].code : (currentUser.bengkung ? getNextBengkungRank(currentUser.bengkung) : 'Putih 1');
            const fee = getUjianBengkungFeeForLevel(target, ujianFeesConfig || undefined);
            return [
              {
                id: 'cand-self',
                name: currentUser.name,
                icNo: currentUser.icNo || '',
                memberId: currentUser.id,
                currentBengkung: cur,
                targetBengkung: target,
                examFee: fee
              }
            ];
          }
        });
      }).catch(() => { });
    } else {
      setCandidates([]);
      setFamilyMembers([]);
      setAvailableChildren([]);
    }
  }, [currentUser]);

  // When DB fees update, automatically update all existing candidates' exam fees
  useEffect(() => {
    if (ujianFeesConfig) {
      setCandidates((prev) =>
        prev.map((c) => ({
          ...c,
          examFee: getUjianBengkungFeeForLevel(c.targetBengkung, ujianFeesConfig)
        }))
      );
    }
  }, [ujianFeesConfig]);

  // Whole family calculations
  const totalCandidates = candidates.length;
  const totalExamFees = candidates.reduce((sum, c) => sum + (Number(c.examFee) || 0), 0);
  const freeFamilyMeals = totalCandidates * pricingConfig.freeMealsPerPax;
  const totalExtraMealCost = extraMeals * pricingConfig.mealPrice;

  // Grand Total for the Whole Family
  const grandTotalWholeFamily = totalExamFees + totalExtraMealCost;

  // Gelanggang Leaders Contact List (Synchronized with ContactPage & Official Instructors)
  const gelanggangContacts: Record<string, { leader: string; phone: string; cleanPhone: string }> = {
    'Pusaka Warisan': {
      leader: 'Jurulatih Fikriz Zaki (Ketua Gelanggang Pusaka Warisan)',
      phone: '013-581 9201',
      cleanPhone: '60135819201'
    },
    'Wadi AnNour': {
      leader: 'Jurulatih Mohd Fauzi (Ketua Gelanggang Wadi AnNour / Setiausaha Induk)',
      phone: '012-447 5157',
      cleanPhone: '60124475157'
    },
    'Paya Besar': {
      leader: 'Jurulatih Mohamad Shakir Fahmi (Ketua Gelanggang Paya Besar)',
      phone: '011-2819 4012',
      cleanPhone: '601128194012'
    },
    'Semua Gelanggang': {
      leader: 'Jurulatih Mohd Fauzi (Setiausaha Induk ASMM)',
      phone: '012-447 5157',
      cleanPhone: '60124475157'
    }
  };

  const selectedContact = gelanggangContacts[gelanggang] || {
    leader: targetLeaderName,
    phone: targetLeaderPhone,
    cleanPhone: cleanLeaderPhone
  };

  // Navigation to login and register with preserved return URL
  const handleGoToLogin = () => {
    sessionStorage.setItem('asmm_intended_url', '/ujian-bengkung');
    if (onNavigateToTab) {
      onNavigateToTab('login', '/login');
    } else {
      window.history.pushState(null, '', '/login');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  const handleGoToRegister = () => {
    sessionStorage.setItem('asmm_intended_url', '/ujian-bengkung');
    if (onNavigateToTab) {
      onNavigateToTab('register', '/register');
    } else {
      window.history.pushState(null, '', '/register');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  // Add Candidate to Family
  const handleAddCandidate = () => {
    if (!currentUser) {
      sessionStorage.setItem('asmm_intended_url', '/ujian-bengkung');
      showToast('warning', 'Log Masuk Diperlukan', 'Sila log masuk ke akaun portal anda terlebih dahulu untuk mendaftar calon pesilat.');
      handleGoToLogin();
      return;
    }

    const defaultCurrent = '';
    const available = getAvailableTargetRanks(defaultCurrent);
    const defaultTarget = available.length > 0 ? available[0].code : 'Hitam 1';
    const fee = getUjianBengkungFeeForLevel(defaultTarget, ujianFeesConfig || undefined);
    setCandidates((prev) => [
      ...prev,
      {
        id: `cand-${Date.now()}`,
        name: '',
        icNo: '',
        currentBengkung: defaultCurrent,
        targetBengkung: defaultTarget,
        examFee: fee
      }
    ]);
  };

  // Select a family member for a candidate row
  const handleSelectFamilyMemberForCandidate = (index: number, member: UserProfile) => {
    const isAlreadySelected = candidates.some(
      (c, i) => i !== index && ((c.icNo && c.icNo === member.icNo) || (c.memberId && c.memberId === member.id))
    );
    if (isAlreadySelected) {
      showToast('info', 'Calon Sudah Dipilih', `${member.name} sudah berada dalam senarai calon.`);
      return;
    }

    const cur = member.bengkung ? cleanBengkungTitle(member.bengkung) : '';
    const available = getAvailableTargetRanks(cur);
    const target = available.length > 0 ? available[0].code : (member.bengkung ? getNextBengkungRank(member.bengkung) : 'Hitam 1');
    const fee = getUjianBengkungFeeForLevel(target, ujianFeesConfig || undefined);

    setCandidates((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        name: member.name,
        icNo: member.icNo || '',
        memberId: member.id,
        currentBengkung: cur,
        targetBengkung: target,
        examFee: fee
      };
      return copy;
    });

    showToast('success', 'Calon Dipilih', `${member.name} (${cur ? 'Tahap Semasa: ' + cur + ' ➔ ' : ''}Sasaran: ${target} - RM ${fee}) dipilih dari profil keluarga.`);
  };

  // Add existing linked child
  const handleAddLinkedChild = (child: UserProfile) => {
    if (!currentUser) {
      sessionStorage.setItem('asmm_intended_url', '/ujian-bengkung');
      showToast('warning', 'Log Masuk Diperlukan', 'Sila log masuk terlebih dahulu.');
      if (onNavigateToTab) onNavigateToTab('login', '/login');
      else {
        window.history.pushState(null, '', '/login');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
      return;
    }

    if (candidates.some((c) => (c.icNo && c.icNo === child.icNo) || (c.memberId && c.memberId === child.id) || c.name.toLowerCase() === child.name.toLowerCase())) {
      showToast('info', 'Calon Sudah Ditambah', `${child.name} sudah berada dalam senarai calon keluarga.`);
      return;
    }

    const cur = child.bengkung ? cleanBengkungTitle(child.bengkung) : '';
    const available = getAvailableTargetRanks(cur);
    const target = available.length > 0 ? available[0].code : (child.bengkung ? getNextBengkungRank(child.bengkung) : 'Hitam 1');
    const fee = getUjianBengkungFeeForLevel(target, ujianFeesConfig || undefined);

    setCandidates((prev) => [
      ...prev.filter((c) => c.name.trim() !== ''),
      {
        id: `cand-${Date.now()}-${child.id}`,
        name: child.name,
        icNo: child.icNo || '',
        memberId: child.id,
        currentBengkung: cur,
        targetBengkung: target,
        examFee: fee
      }
    ]);
    showToast('success', 'Calon Ditambah', `${child.name} dimasukkan ke dalam senarai.`);
  };

  // Remove Candidate
  const handleRemoveCandidate = (index: number) => {
    if (candidates.length <= 1) {
      showToast('warning', 'Peringatan', 'Sekurang-kurangnya satu calon pesilat diperlukan.');
      return;
    }
    setCandidates((prev) => prev.filter((_, i) => i !== index));
  };

  // Update candidate detail
  const handleUpdateCandidate = (index: number, field: keyof FamilyCandidate, value: any) => {
    setCandidates((prev) => {
      const copy = [...prev];
      const cand = { ...copy[index], [field]: value };

      if (field === 'currentBengkung') {
        const available = getAvailableTargetRanks(value);
        const isTargetStillValid = available.some((r) => r.code === cand.targetBengkung);
        if (!isTargetStillValid) {
          cand.targetBengkung = available.length > 0 ? available[0].code : 'Peringkat Tertinggi';
        }
        cand.examFee = getUjianBengkungFeeForLevel(cand.targetBengkung, ujianFeesConfig || undefined);
      } else if (field === 'targetBengkung') {
        cand.examFee = getUjianBengkungFeeForLevel(value, ujianFeesConfig || undefined);
      }

      copy[index] = cand;
      return copy;
    });
  };

  // Copy Account Number
  const handleCopyAccountNo = async () => {
    const acc = targetAccountNo;
    const bank = targetBankName;
    try {
      await navigator.clipboard.writeText(acc);
      setIsAccountCopied(true);
      showToast('success', 'No. Akaun Gelanggang Disalin!', `${bank} (${acc} - ${targetAccountHolder}) sedia ditampal di aplikasi perbankan anda.`);
      setTimeout(() => setIsAccountCopied(false), 3000);
    } catch {
      showToast('error', 'Ralat', 'Gagal menyalin no akaun.');
    }
  };

  const [isRefNotesCopied, setIsRefNotesCopied] = useState(false);

  // Rujukan Bayaran: combine UB2026 with Member ID digits (e.g. ASMM-011 -> UB2026011)
  const candidateRefText = useMemo(() => {
    // 1. Check primary candidate's memberId or currentUser's id
    const targetMemberId = candidates[0]?.memberId || currentUser?.id || candidates.find(c => c.memberId)?.memberId;
    if (targetMemberId) {
      const digits = targetMemberId.replace(/\D/g, '');
      if (digits) {
        // e.g. ASMM-011 -> '011', 11 -> '011', 1 -> '001'
        const paddedDigits = digits.length < 3 ? digits.padStart(3, '0') : digits.slice(-4);
        return `UB2026${paddedDigits}`;
      }
      const cleanAlpha = targetMemberId.replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase();
      if (cleanAlpha) {
        return `UB2026${cleanAlpha}`;
      }
    }

    // 2. Fallback to IC number digits (last 4 digits)
    const ic = candidates[0]?.icNo || currentUser?.icNo;
    if (ic) {
      const icDigits = ic.replace(/\D/g, '');
      if (icDigits.length >= 4) {
        return `UB2026${icDigits.slice(-4)}`;
      }
    }

    return 'UB2026011';
  }, [candidates, currentUser]);

  const handleCopyRefText = async () => {
    try {
      await navigator.clipboard.writeText(candidateRefText);
      setIsRefNotesCopied(true);
      showToast('success', 'Rujukan Bayaran Disalin!', `${candidateRefText} sedia ditampal di aplikasi perbankan.`);
      setTimeout(() => setIsRefNotesCopied(false), 2500);
    } catch {
      showToast('error', 'Ralat', 'Gagal menyalin rujukan.');
    }
  };

  // Receipt upload & verification state (Option B: DuitNow QR)
  const [uploadedReceiptFile, setUploadedReceiptFile] = useState<File | null>(null);
  const [receiptFilePreview, setReceiptFilePreview] = useState<string | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState<boolean>(false);
  const [isReceiptSubmitted, setIsReceiptSubmitted] = useState<boolean>(false);
  const [submittedInvoice, setSubmittedInvoice] = useState<InvoiceItem | null>(null);
  const [isScanningPdf, setIsScanningPdf] = useState<boolean>(false);
  const [scanProgress, setScanProgress] = useState<string>('');
  const [, setScanStatus] = useState<'idle' | 'success' | 'empty' | 'error'>('idle');

  const initialTimeObj = parseReceiptTimestamp('');
  const [receiptRefId, setReceiptRefId] = useState<string>('');
  const [paidDate, setPaidDate] = useState<string>(initialTimeObj.date);
  const [paidTime, setPaidTime] = useState<string>(initialTimeObj.time);

  // Sync receiptRefId with candidateRefText if not yet customized
  useEffect(() => {
    if (!receiptRefId || receiptRefId.startsWith('UB2026')) {
      setReceiptRefId(candidateRefText);
    }
  }, [candidateRefText]);

  // Handle receipt file change
  const handleReceiptFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Rule: PDF atau Imej (.pdf, .png, .jpg, .jpeg, .webp)
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = file.type.startsWith('image/') || /\.(png|jpe?g|webp|bmp)$/i.test(file.name);
    if (!isPdf && !isImage) {
      showToast('error', 'Format Tidak Sah', 'Bukti bayaran mesti dimuat naik dalam bentuk dokumen PDF (.pdf) atau fail imej (.png, .jpg, .jpeg, .webp).');
      e.target.value = '';
      return;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      showToast('error', 'Saiz Fail Melebihi Had (Maksimum 10MB)', `Saiz fail (${(file.size / (1024 * 1024)).toFixed(1)} MB) melebihi had maksimum 10MB.`);
      e.target.value = '';
      return;
    }

    setUploadedReceiptFile(file);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = () => {
        setReceiptFilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setReceiptFilePreview(null);
    }

    setIsScanningPdf(true);
    setScanProgress('Membaca butiran resit bayaran...');
    try {
      const scanned = await scanReceiptFile(file, (msg, pct) => {
        setScanProgress(`${msg} (${pct}%)`);
      });

      if (scanned.referenceId) {
        setReceiptRefId(scanned.referenceId);
      }
      if (scanned.timestamp) {
        const parsed = parseReceiptTimestamp(scanned.timestamp);
        setPaidDate(parsed.date);
        setPaidTime(parsed.time);
      }

      if (scanned.referenceId || scanned.timestamp) {
        setScanStatus('success');
        showToast(
          'success',
          'Data Resit Dikenalpasti!',
          `No. Rujukan: ${scanned.referenceId || receiptRefId || candidateRefText} • Tarikh: ${scanned.timestamp || paidDate}`
        );
      } else {
        setScanStatus('empty');
      }
    } catch (err) {
      console.warn('[UjianBengkung] Scan receipt error:', err);
      setScanStatus('error');
    } finally {
      setIsScanningPdf(false);
    }
  };

  // Upload and submit receipt to Google Drive JSON Ahli vault and record invoice & transaction
  const handleUploadAndSubmitReceipt = async () => {
    const invalidCandidate = candidates.find((c) => !c.name.trim());
    if (!parentName.trim() || invalidCandidate) {
      showToast('warning', 'Maklumat Belum Lengkap', 'Sila pastikan nama waris dan setiap calon pesilat telah diisi terlebih dahulu.');
      return;
    }

    if (!uploadedReceiptFile) {
      showToast('error', 'Resit Diperlukan', 'Sila pilih atau tangkap fail resit bayaran (PDF atau Imej) sebelum menghantar.');
      return;
    }

    setIsUploadingReceipt(true);
    try {
      const primaryCandidate = candidates[0];
      const targetMemberId = primaryCandidate?.memberId || currentUser?.id || `guest-${Date.now()}`;
      const targetIc = primaryCandidate?.icNo || currentUser?.icNo || '';
      const finalRefId = (receiptRefId.trim() || candidateRefText).toUpperCase();
      const today = new Date().toISOString().split('T')[0];
      const cleanTime = paidTime.length === 5 ? `${paidTime}:00` : (paidTime || '10:00:00');
      const finalPaidAt = `${paidDate || today} ${cleanTime}`;

      // Upload receipt file to Google Drive JSON Ahli vault
      const uploadRes = await uploadYuranReceiptToAhliDriveJson(
        uploadedReceiptFile,
        targetMemberId,
        parentName.trim() || primaryCandidate.name || 'Waris Pesilat',
        'Disember',
        '2026',
        targetIc
      );

      // Build invoice line items
      const invoiceItems: { id: string; description: string; quantity: number; unitPrice: number; amount: number }[] = [];
      candidates.forEach((c, idx) => {
        invoiceItems.push({
          id: `item-cand-${idx + 1}`,
          description: `Yuran Ujian ${c.targetBengkung} - ${c.name} (Termasuk 2 Pek Makanan Percuma & Tempat Duduk Waris)`,
          quantity: 1,
          unitPrice: Number(c.examFee) || 0,
          amount: Number(c.examFee) || 0
        });
      });
      if (extraMeals > 0) {
        invoiceItems.push({
          id: `item-extra-meals`,
          description: `Pek Makanan Katering Tambahan`,
          quantity: extraMeals,
          unitPrice: pricingConfig.mealPrice,
          amount: totalExtraMealCost
        });
      }

      const invNo = `INV-UBF-${Date.now().toString().slice(-6)}`;
      const dueDate = eventMeta.registrationDeadline || '2026-12-10';

      const newInvoice: InvoiceItem = {
        id: `inv-ubf-${Date.now()}`,
        invoiceNo: invNo,
        memberId: targetMemberId,
        memberName: parentName.trim() || primaryCandidate.name || 'Waris Pesilat',
        memberPhone: parentPhone.trim() || currentUser?.phone || '',
        memberIcNo: targetIc,
        category: 'Yuran Bengkung',
        title: `Pendaftaran Ujian Bengkung & Kemudahan Keluarga (${totalCandidates} Calon)`,
        description: `Pendaftaran Ujian Bengkung sekeluarga: ${candidates.map((c) => c.name).join(', ')}. Termasuk ${freeFamilyMeals} pek makanan percuma & tempat duduk waris disediakan di dewan${extraMeals > 0 ? `, serta ${extraMeals} pek makanan tambahan.` : '.'}`,
        amount: grandTotalWholeFamily,
        status: 'Menunggu Pengesahan',
        paymentMethod: 'DuitNow QR',
        toyibBillCode: finalRefId,
        transactionRef: finalRefId,
        receiptUrl: uploadRes.fileUrl,
        receiptFileName: uploadRes.fileName,
        paidAt: finalPaidAt,
        dueDate: dueDate,
        gelanggang: gelanggang,
        createdAt: new Date().toISOString(),
        items: invoiceItems
      };

      await saveInvoiceDB(newInvoice);

      // Save income transaction
      await saveTransactionDB({
        id: `ASMM-TX-${Date.now()}`,
        date: paidDate || today,
        category: 'Yuran Bengkung',
        description: `Bayaran Ujian Bengkung Gelanggang ${gelanggang}: ${parentName} (${invNo}) - Ref: ${finalRefId}`,
        amount: grandTotalWholeFamily,
        type: 'Income',
        payerOrPayee: parentName.trim(),
        gelanggang: gelanggang,
        recordedBy: currentUser?.name || 'Waris Pesilat',
        status: 'Menunggu Pengesahan',
        receiptUrl: uploadRes.fileUrl,
        toyibBillCode: finalRefId
      });

      setIsReceiptSubmitted(true);
      setSubmittedInvoice(newInvoice);

      showToast(
        'success',
        'Resit Berjaya Dihantar!',
        `Fail ${uploadRes.fileName} telah disimpan. No. Invois: ${invNo}. Bayaran disalurkan ke Gelanggang ${gelanggang}.`
      );
    } catch (err: any) {
      console.error('[UjianBengkung] Error uploading receipt:', err);
      showToast('error', 'Gagal Memuat Naik Resit', err?.message || 'Berlaku ralat semasa menyimpan resit bayaran.');
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  const handleSendSubmittedReceiptWhatsApp = () => {
    if (!submittedInvoice) return;
    const msg = `*PENGESAHAN BAYARAN UJIAN BENGKUNG (GELANGGANG ${submittedInvoice.gelanggang.toUpperCase()})*
--------------------------------------------
*No. Invois:* ${submittedInvoice.invoiceNo}
*Nama Waris:* ${submittedInvoice.memberName}
*No. Telefon:* ${submittedInvoice.memberPhone || parentPhone}
*Gelanggang:* ${submittedInvoice.gelanggang}
*Penerima Bayaran:* ${targetAccountHolder} (${targetBankName} - ${targetAccountNo})
*Jumlah Bayaran:* RM ${submittedInvoice.amount}.00
*No. Rujukan Transaksi:* ${submittedInvoice.transactionRef || receiptRefId || candidateRefText}
*Tarikh & Masa Bayaran:* ${submittedInvoice.paidAt || `${paidDate} ${paidTime}`}
*Senarai Calon (${totalCandidates} Orang):*
${candidates.map((c, i) => `  ${i + 1}. ${c.name} (${c.targetBengkung})`).join('\n')}
*Kemudahan:* ${freeFamilyMeals} Pek Makanan Percuma & Tempat Duduk Waris Percuma di Dewan${extraMeals > 0 ? ` + ${extraMeals} Makanan Tambahan` : ''}

*Status:* Resit telah dimuat naik ke Portal ASMM (Menunggu Pengesahan Pengurusan Gelanggang).
${submittedInvoice.receiptUrl ? `*Pautan Resit:* ${submittedInvoice.receiptUrl}\n` : ''}
Mohon pihak pengurusan Gelanggang ${submittedInvoice.gelanggang} mengesahkan pendaftaran Ujian Bengkung keluarga kami. Terima kasih.`;

    const encoded = encodeURIComponent(msg);
    const waUrl = `https://wa.me/${selectedContact.cleanPhone}?text=${encoded}`;
    window.open(waUrl, '_blank');
  };

  // Generate official formatted text for WhatsApp or clipboard
  const generateFamilyConfirmationMessage = (isQrPayment = false) => {
    const candidateListText = candidates.map((c, idx) => {
      return `  ${idx + 1}. ${c.name.trim() || '[Nama Calon]'} (${c.icNo ? `IC: ${c.icNo}, ` : ''}${c.targetBengkung} - RM ${c.examFee})`;
    }).join('\n');

    return `*PENDAFTARAN UJIAN BENGKUNG & TEMPAHAN KELUARGA ASMM*
--------------------------------------------
*Maklumat Waris / Penjaga:*
• Nama Waris: ${parentName.trim() || '[Belum diisi]'}
• No. Telefon: ${parentPhone.trim() || '[Belum diisi]'}
• Gelanggang: Gelanggang ${gelanggang}
• Pengurusan Gelanggang: ${selectedContact.leader}

*Senarai Calon Pesilat Keluarga (${totalCandidates} Orang):*
${candidateListText}
• Subtotal Yuran Calon: RM ${totalExamFees}

*Kemudahan Percuma (Termasuk Pakej Calon):*
• Pek Makanan Percuma: ${freeFamilyMeals} Pek (${pricingConfig.freeMealsPerPax} pek x ${totalCandidates} calon) = PERCUMA
• Tempat Duduk Waris: Disediakan Percuma di Dewan (Tiada jualan kerusi)
${extraMeals > 0 ? `\n*Tempahan Tambahan (Add-On Keluarga):*\n• Makanan Tambahan: ${extraMeals} pek x RM ${pricingConfig.mealPrice} = RM ${totalExtraMealCost}\n` : ''}
*KIRAAN JUMLAH KESELURUHAN KELUARGA:*
• *JUMLAH KESELURUHAN: RM ${grandTotalWholeFamily}*
${isQrPayment ? `• Kaedah Bayaran: Pindahan / DuitNow QR Gelanggang ${gelanggang} (${targetBankName} - ${targetAccountNo})\n• Pemegang Akaun: ${targetAccountHolder}\n• Rujukan Bayaran: ${candidateRefText}\n• Sila lampirkan resit slip bayaran bersama mesej ini.\n` : ''}${specialNotes.trim() ? `• Catatan Khas: ${specialNotes.trim()}\n` : ''}
Mohon pihak pengurusan Gelanggang ${gelanggang} mengesahkan pendaftaran Ujian Bengkung bagi keluarga kami. Terima kasih.`;
  };

  // Direct WhatsApp dispatch
  const handleSendWhatsApp = (isQrPayment = false) => {
    const invalidCandidate = candidates.find((c) => !c.name.trim());
    if (!parentName.trim() || invalidCandidate) {
      showToast('warning', 'Maklumat Belum Lengkap', 'Sila lengkapkan nama waris dan calon pesilat terlebih dahulu.');
      return;
    }
    const msg = generateFamilyConfirmationMessage(isQrPayment);
    const encoded = encodeURIComponent(msg);
    const waUrl = `https://wa.me/${selectedContact.cleanPhone}?text=${encoded}`;
    window.open(waUrl, '_blank');
    showToast('info', 'Membuka WhatsApp', `Menghubungkan ke ${selectedContact.leader}...`);
  };

  // Online Payment / Create Invoice
  const handleCreateFamilyInvoiceAndPay = async () => {
    const invalidCandidate = candidates.find((c) => !c.name.trim());
    if (!parentName.trim() || invalidCandidate) {
      showToast('warning', 'Maklumat Belum Lengkap', 'Sila pastikan nama waris dan setiap calon pesilat telah diisi.');
      return;
    }

    setIsProcessingPayment(true);
    try {
      const invNo = `INV-UBF-${Date.now().toString().slice(-6)}`;
      const dueDate = eventMeta.registrationDeadline || '2026-12-10';

      // Build invoice line items
      const invoiceItems: { id: string; description: string; quantity: number; unitPrice: number; amount: number }[] = [];

      // 1. Line item for each candidate exam fee
      candidates.forEach((c, idx) => {
        invoiceItems.push({
          id: `item-cand-${idx + 1}`,
          description: `Yuran Ujian ${c.targetBengkung} - ${c.name} (Termasuk 2 Pek Makanan Percuma & Tempat Duduk Waris)`,
          quantity: 1,
          unitPrice: Number(c.examFee) || 0,
          amount: Number(c.examFee) || 0
        });
      });

      // 2. Extra meals if any
      if (extraMeals > 0) {
        invoiceItems.push({
          id: `item-extra-meals`,
          description: `Pek Makanan Katering Tambahan`,
          quantity: extraMeals,
          unitPrice: pricingConfig.mealPrice,
          amount: totalExtraMealCost
        });
      }

      const primaryCandidate = candidates[0];
      const newInvoice: InvoiceItem = {
        id: `inv-ubf-${Date.now()}`,
        invoiceNo: invNo,
        memberId: currentUser?.id || `guest-${Date.now()}`,
        memberName: parentName.trim() || primaryCandidate.name || 'Waris Pesilat',
        memberPhone: parentPhone.trim() || currentUser?.phone || '',
        memberIcNo: primaryCandidate.icNo || '',
        category: 'Yuran Bengkung',
        title: `Pendaftaran Ujian Bengkung & Kemudahan Keluarga (${totalCandidates} Calon)`,
        description: `Pendaftaran Ujian Bengkung sekeluarga: ${candidates.map((c) => c.name).join(', ')}. Termasuk ${freeFamilyMeals} pek makanan percuma & tempat duduk waris disediakan di dewan${extraMeals > 0 ? `, serta ${extraMeals} pek makanan tambahan.` : '.'}`,
        amount: grandTotalWholeFamily,
        status: 'Belum Dibayar',
        dueDate: dueDate,
        gelanggang: gelanggang,
        createdAt: new Date().toISOString(),
        items: invoiceItems
      };

      await saveInvoiceDB(newInvoice);

      showToast(
        'success',
        'Invois Keluarga Dicipta!',
        `Invois berjaya dijana dengan jumlah RM ${grandTotalWholeFamily}. Membuka gerbang pembayaran...`
      );

      // Navigate to payments page with invoice selected
      if (onNavigateToTab) {
        onNavigateToTab('payments', `/payments?inv=${newInvoice.id}`);
      } else {
        window.history.pushState(null, '', `/payments?inv=${newInvoice.id}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch {
      showToast('error', 'Ralat', 'Gagal memproses invois. Sila cuba lagi atau hubungi pengurusan melalui WhatsApp.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="bg-white min-h-screen text-slate-900 pb-20">
      {/* Comprehensive SEO Meta Tags Injection */}
      <SEO
        title="Pendaftaran Ujian Kenaikan Bengkung Rasmi | PSSASMM"
        description="Pendaftaran rasmi Ujian Kenaikan Bengkung ASMM kini dibuka kepada semua pelatih yang layak. Pakej pendaftaran merangkumi 2 pek makanan percuma bagi setiap calon dan tempat duduk waris percuma di dewan."
        keywords="ujian bengkung silat, ujian kenaikan bengkung asmm, silat awang sulung merah muda, gelanggang silat kedah, yuran ujian silat, bengkung silat, kemudahan keluarga silat, duitnow qr gelanggang"
        ogImage="/logo-pssasmm.png"
        canonicalUrl="https://asmm.pages.dev/ujian-bengkung"
      />

      {/* Container with constrained width and generous whitespace */}
      <div className="max-w-4xl mx-auto px-3.5 sm:px-6 py-4 sm:py-10">

        {/* 1. HERO FEATURE CARD (Centerpiece with Overlapping Badge) */}
        <div className="relative pt-10 sm:pt-14 mb-8 sm:mb-10">
          {/* Overlapping Martial Rank Badge / Avatar Anchor */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-4 ring-white shadow-xl bg-white p-1.5 flex items-center justify-center">
              <img
                src="/logo-pssasmm.png"
                alt="PSSASMM"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Main Pure White Rounded Card */}
          <div className="bg-white rounded-3xl border border-slate-200/80 p-5 sm:p-10 pt-14 sm:pt-16 shadow-sm relative text-center">
            {/* Top Right High-Contrast Status (Centered on mobile below avatar, pinned top-right on sm+) */}
            <div className="flex justify-center sm:absolute sm:top-5 sm:right-6 mb-3 sm:mb-0">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 text-[11px] sm:text-xs font-bold border border-red-100 shadow-3xs">
                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                <span>Pendaftaran Rasmi Dibuka</span>
              </span>
            </div>

            {/* Official ROS Trust Tag */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-[10px] sm:text-xs font-semibold mb-3 max-w-full text-center leading-tight">
              <ShieldCheck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span>Persatuan Seni Silat Awang Sulung Merah Muda (ROS: PPM-007-02-28082022)</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight max-w-2xl mx-auto break-words">
              Pendaftaran Ujian Bengkung & Kemudahan Keluarga
            </h1>

            {/* Official Announcement Text / Greeting */}
            <p className="text-xs sm:text-base text-slate-600 font-medium mt-2.5 sm:mt-3 max-w-xl mx-auto leading-relaxed">
              Assalamualaikum w.b.t. dan Salam Sejahtera. Pendaftaran rasmi bagi Ujian Kenaikan Bengkung kini dibuka kepada semua pelatih yang layak. Setiap calon dibekalkan 2 pek makanan percuma serta tempat duduk waris percuma di dewan.
            </p>

            {/* Floating Metric Bar (Official ASMM Colors: Merah, Kuning, Biru, Hitam) */}
            <div className="bg-white rounded-2xl sm:rounded-full py-3 px-3 sm:px-8 shadow-sm border border-slate-200 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-around gap-2.5 sm:gap-6 max-w-2xl mx-auto mt-6">
              <div className="text-center p-1">
                <span className="text-sm sm:text-lg font-black text-red-600 block sm:inline">{pricingConfig.freeMealsPerPax} Pek Makanan</span>
                <span className="text-[10px] sm:text-xs text-slate-500 font-medium sm:ml-1.5 block sm:inline">Percuma / Calon</span>
              </div>
              <div className="hidden sm:block h-5 w-px bg-slate-200" />
              <div className="text-center p-1">
                <span className="text-sm sm:text-lg font-black text-amber-600 block sm:inline">Tempat Duduk Waris</span>
                <span className="text-[10px] sm:text-xs text-slate-500 font-medium sm:ml-1.5 block sm:inline">Percuma di Dewan</span>
              </div>
              <div className="hidden sm:block h-5 w-px bg-slate-200" />
              <div className="text-center p-1">
                <span className="text-sm sm:text-lg font-black text-blue-600 block sm:inline">Pentauliahan Rasmi</span>
                <span className="text-[10px] sm:text-xs text-slate-500 font-medium sm:ml-1.5 block sm:inline">Sijil & Bengkung</span>
              </div>
              <div className="hidden sm:block h-5 w-px bg-slate-200" />
              <div className="text-center p-1">
                <span className="text-sm sm:text-lg font-black text-slate-900 block sm:inline">Bayaran Terus</span>
                <span className="text-[10px] sm:text-xs text-slate-500 font-medium sm:ml-1.5 block sm:inline">Ke Gelanggang Anda</span>
              </div>
            </div>

            {/* Event Schedule & Venue Information Bar (Editable via /notices) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2.5 max-w-3xl mx-auto mt-6 pt-5 border-t border-slate-100 text-left">
              <div className="p-2.5 sm:p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2 sm:gap-2.5 min-w-0">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 border border-red-100">
                  <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Tarikh Acara</span>
                  <span className="text-[11px] sm:text-xs font-black text-slate-900 truncate block">
                    {formatEventDates(eventMeta.eventDate, eventMeta.eventEndDate) || '11 & 12 Dis 2026'}
                  </span>
                </div>
              </div>

              <div className="p-2.5 sm:p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2 sm:gap-2.5 min-w-0">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-100">
                  <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Masa Ujian</span>
                  <span className="text-[11px] sm:text-xs font-black text-slate-900 truncate block" title={eventMeta.eventTime}>
                    {eventMeta.eventTime}
                  </span>
                </div>
              </div>

              <div className="p-2.5 sm:p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2 sm:gap-2.5 min-w-0">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Lokasi Majlis</span>
                  <span className="text-[11px] sm:text-xs font-black text-slate-900 truncate block" title={eventMeta.eventLocation}>
                    {eventMeta.eventLocation}
                  </span>
                </div>
              </div>

              <div className="p-2.5 sm:p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2 sm:gap-2.5 min-w-0">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0 border border-slate-200">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Tutup Pendaftaran</span>
                  <span className="text-[11px] sm:text-xs font-black text-slate-900 truncate block">
                    {eventMeta.registrationDeadline ? new Date(eventMeta.registrationDeadline).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '15 Dis 2026'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2. SECTION: MAKLUMAT PAKEJ PENDAFTARAN CALON */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-red-600" />
                <span>Maklumat Pakej Pendaftaran Calon</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Setiap pendaftaran calon Ujian Bengkung telah merangkumi kemudahan percuma untuk ahli keluarga seperti berikut:
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Free Meals Card */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs hover:border-red-200 transition flex items-start gap-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0 text-red-600">
                <Utensils className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-base font-extrabold text-slate-900">{pricingConfig.freeMealsPerPax} Pek Makanan</h3>
                  <span className="px-2.5 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-black uppercase tracking-wider">
                    Percuma
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  {pricingConfig.freeMealsPerPax} pek hidangan katering lengkap (makanan dan minuman berbungkus) disediakan untuk ahli keluarga bagi setiap calon pesilat yang didaftarkan.
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs font-bold text-slate-900">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>
                    {totalCandidates > 1
                      ? `Keluarga dengan ${totalCandidates} calon automatik dapat ${freeFamilyMeals} pek makanan percuma!`
                      : `1 calon automatik dapat ${pricingConfig.freeMealsPerPax} pek makanan percuma!`
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Free Seats Card (Tiada Jualan Tempat Duduk) */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs hover:border-amber-200 transition flex items-start gap-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0 text-amber-600">
                <Armchair className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-base font-extrabold text-slate-900">Tempat Duduk Waris</h3>
                  <span className="px-2.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-black uppercase tracking-wider">
                    Percuma di Dewan
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Ruang tempat duduk di dalam dewan disediakan secara percuma untuk ibu bapa dan waris hadir menyaksikan sesi ujian (Tiada sebarang caj atau jualan tempat duduk).
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs font-bold text-slate-900">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Disediakan percuma untuk semua waris calon pesilat</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. SECTION: SUSUNAN TENTATIF PROGRAM & PERINGATAN SARAPAN */}
        <div className="mb-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-red-600" />
                <span>Susunan Tentatif Program</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Jadual rasmi perjalanan Ujian Kenaikan Bengkung & Majlis Penganugerahan Sandang.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">
                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                <span>Sesi Pagi & Petang</span>
              </span>
            </div>
          </div>

          {/* Notice: Sarapan Siap-siap Sebelum Hadir */}
          <div className="mb-6 p-4 sm:p-5 rounded-2xl bg-amber-50/80 border-2 border-amber-300 shadow-xs flex items-start gap-3.5">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-xs">
              <Coffee className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full bg-amber-200/90 text-amber-900 text-[10px] sm:text-[11px] font-black uppercase tracking-wider">
                  Peringatan Kehadiran
                </span>
                <span className="text-xs font-bold text-amber-900 hidden sm:inline">• Pendaftaran 7:30 Pagi</span>
              </div>
              <h4 className="text-sm sm:text-base font-extrabold text-slate-900 mt-1">
                Diminta Peserta Makan Sarapan Siap-Siap Sebelum Hadir
              </h4>
              <p className="text-xs sm:text-sm text-slate-700 mt-1 leading-relaxed">
                Semua peserta dan calon pesilat <strong>diminta makan sarapan siap-siap sebelum hadir</strong> ke dewan/gelanggang pada jam 7:30 pagi agar kekal cergas dan bersedia untuk adat penerimaan serta ujian fizikal. Pek hidangan makanan katering tengah hari yang lengkap akan diagihkan sebelum waktu rehat tengah hari.
              </p>
            </div>
          </div>

          {/* Single Column Timeline Container */}
          <div className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-7 shadow-xs">
            {/* --- SESI PAGI --- */}
            <div className="mb-8">
              {/* Sesi Pagi Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 gap-2">
                <div>
                  <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider block">Bahagian 1</span>
                  <h3 className="text-base sm:text-lg font-black text-slate-900">
                    SESI PAGI: Adat Penerimaan & Ujian Fizikal
                  </h3>
                </div>
                <span className="self-start sm:self-auto px-3 py-1 bg-amber-50 text-amber-800 text-xs font-bold rounded-full border border-amber-200">
                  07:30 AM – 02:00 PM
                </span>
              </div>

              {/* Sesi Pagi Timeline Items */}
              <div className="mt-5 space-y-5">
                {/* 07:30 AM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5 pb-4 border-b border-slate-100">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      07:30 AM – 08:00 AM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">Pendaftaran Peserta</h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Pendaftaran calon pesilat, semakan nombor tempat duduk & agihan kupon makanan.
                    </p>
                  </div>
                </div>

                {/* 08:00 AM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5 pb-4 border-b border-slate-100">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-amber-900 bg-amber-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      08:00 AM – 09:30 AM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Upacara Penerimaan Anak Murid (Mandi Limau)
                    </h4>
                    <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                        <span>Istiadat Buka Gelanggang</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                        <span>Sesi Mandi Limau <strong className="text-slate-900 font-bold">(5 Calon Baru)</strong></span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                        <span>Salam Guru, Akad Penerimaan & Bacaan Ikrar</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 09:30 AM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5 pb-4 border-b border-slate-100">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-red-900 bg-red-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      09:30 AM – 12:45 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Ujian Kenaikan Bengkung <span className="text-red-600 font-bold">(15 Calon)</span>
                    </h4>
                    <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"></span>
                        <span><strong>Kumpulan 1:</strong> Tahap 1 (1 calon) & Tahap 2 (8 calon)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"></span>
                        <span><strong>Kumpulan 2:</strong> Tahap 3 (6 calon)</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 12:45 PM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-blue-900 bg-blue-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      12:45 PM – 02:00 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-extrabold text-slate-900">
                        REHAT - Makan Tengah Hari & Solat Zohor Berjemaah
                      </h4>
                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-black uppercase tracking-wider">
                        Katering
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Pengagihan pek hidangan makan tengah hari katering berbungkus kepada waris & pesilat, serta Solat Zohor berjemaah.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* --- SESI PETANG --- */}
            <div className="pt-6 border-t border-slate-200/80">
              {/* Sesi Petang Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 gap-2">
                <div>
                  <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider block">Bahagian 2</span>
                  <h3 className="text-base sm:text-lg font-black text-slate-900">
                    SESI PETANG: Pengisian Minda & Majlis Kemuncak (Grand Finale)
                  </h3>
                </div>
                <span className="self-start sm:self-auto px-3 py-1 bg-red-50 text-red-800 text-xs font-bold rounded-full border border-red-200 whitespace-nowrap">
                  02:00 PM – 05:30 PM
                </span>
              </div>

              {/* Sesi Petang Timeline Items */}
              <div className="mt-5 space-y-5">
                {/* 02:00 PM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5 pb-4 border-b border-slate-100">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      02:00 PM – 03:00 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Sharing Talk & Amanat Barisan Jurulatih
                    </h4>
                    <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0"></span>
                        <span>Perkongsian ilmu, sejarah & motivasi persilatan</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0"></span>
                        <span>Urusetia: Kira markah & susun <strong className="text-slate-900 font-bold">32 sandang & sijil</strong></span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 03:00 PM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5 pb-4 border-b border-slate-100">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-red-900 bg-red-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      03:00 PM – 05:00 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-black text-slate-900">
                        MAJLIS KEMUNCAK & PENGANUGERAHAN
                      </h4>
                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] font-black uppercase tracking-wider">
                        Kemuncak
                      </span>
                    </div>
                    <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"></span>
                        <span>Ucapan Perasmian Penutup</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"></span>
                        <span>Pengumuman Keputusan & Pemakaian Bengkung (Tahap 1, 2, 3)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"></span>
                        <span>Penganugerahan Sandang</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 05:00 PM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      05:00 PM – 05:30 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Sesi Bergambar, Tutup Gelanggang & Bersurai
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Sesi fotografi kenangan bersama guru, jurulatih dan pesilat serta istiadat penutupan gelanggang rasmi.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 5. SECTION: FORM PENDAFTARAN CALON-CALON SEKELUARGA */}
        <div className="mb-10 bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <div className="border-b border-slate-100 pb-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-red-600" />
                <span>Maklumat Calon Pesilat Sekeluarga</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Daftarkan seorang atau lebih calon pesilat dalam keluarga anda. Setiap pendaftaran calon menerima {pricingConfig.freeMealsPerPax} pek makanan katering percuma dan tempat duduk waris percuma di dewan.
              </p>
            </div>
            {currentUser && (
              <button
                type="button"
                onClick={handleAddCandidate}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition shadow-xs cursor-pointer self-start sm:self-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Tambah Calon</span>
              </button>
            )}
          </div>

          {!currentUser ? (
            /* Login Gate: User MUST log in to register candidates */
            <div className="p-6 sm:p-10 rounded-3xl border-2 border-dashed border-red-200 bg-red-50/30 text-center max-w-2xl mx-auto my-4">
              <div className="w-16 h-16 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4 shadow-xs">
                <Lock className="w-8 h-8" />
              </div>
              <span className="inline-block px-3 py-1 bg-red-100 text-red-700 text-[11px] font-black uppercase tracking-wider rounded-full mb-2">
                Log Masuk Diperlukan
              </span>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 mb-2">
                Sila Log Masuk untuk Mendaftar Calon Pesilat
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed max-w-lg mx-auto mb-6">
                Untuk mendaftarkan nama calon pesilat dan membuat tempahan pakej keluarga, anda perlu log masuk ke akaun portal ASMM. Sistem akan memautkan maklumat anak/keluarga serta mencadangkan tahap bengkung dan yuran yang tepat secara automatik.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
                <button
                  type="button"
                  onClick={handleGoToLogin}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm font-bold rounded-2xl shadow-md hover:shadow-lg transition cursor-pointer"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Log Masuk Akaun Portal</span>
                </button>
                <button
                  type="button"
                  onClick={handleGoToRegister}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 text-xs sm:text-sm font-bold rounded-2xl shadow-xs transition cursor-pointer"
                >
                  <UserPlus className="w-4 h-4 text-slate-600" />
                  <span>Daftar Akaun Baharu</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-6 border-t border-red-100 text-left">
                <div className="p-3 bg-white rounded-xl border border-red-100/80 shadow-2xs">
                  <span className="block text-xs font-bold text-slate-900">1. Carian Keluarga Mudah</span>
                  <span className="text-[11px] text-slate-500 leading-tight block mt-0.5">
                    Pilih nama anak terus daripada profil keluarga tanpa perlu mengisi semula nombor MyKid.
                  </span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-red-100/80 shadow-2xs">
                  <span className="block text-xs font-bold text-slate-900">2. Yuran DB Dinamik</span>
                  <span className="text-[11px] text-slate-500 leading-tight block mt-0.5">
                    Kadar yuran dikira secara automatik mengikut penetapan terkini pangkalan data ASMM.
                  </span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-red-100/80 shadow-2xs">
                  <span className="block text-xs font-bold text-slate-900">3. Invois Rasmi ASMM</span>
                  <span className="text-[11px] text-slate-500 leading-tight block mt-0.5">
                    Invois dan resit rasmi berdaftar ROS dijana serta-merta untuk simpanan peribadi waris.
                  </span>
                </div>
              </div>
            </div>
          ) : (
            /* Logged in view with Family Member Search & Dynamic Bengkung Fees */
            <div>
              {/* Quick Add from Linked Family Members */}
              {familyMembers.length > 0 && (
                <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-2.5">
                    <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-red-600" />
                      <span>Pilih Pantas Dari Senarai Keluarga Anda ({familyMembers.length} Ahli):</span>
                    </span>
                    <span className="text-[11px] text-slate-500">
                      Klik nama untuk tambah ke senarai calon dengan yuran automatik
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {familyMembers.map((member) => {
                      const isAlreadyAdded = candidates.some(
                        (c) =>
                          (c.icNo && c.icNo === member.icNo) ||
                          (c.memberId && c.memberId === member.id) ||
                          (c.name && c.name.trim().toLowerCase() === member.name.trim().toLowerCase())
                      );
                      return (
                        <button
                          key={member.id}
                          type="button"
                          disabled={isAlreadyAdded}
                          onClick={() => handleAddLinkedChild(member)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${isAlreadyAdded
                            ? 'bg-slate-200 text-slate-400 border border-slate-200 cursor-not-allowed'
                            : 'bg-white hover:bg-red-50 hover:border-red-200 border border-slate-200 text-slate-800 shadow-2xs'
                            }`}
                        >
                          <UserCheck className={`w-3.5 h-3.5 ${isAlreadyAdded ? 'text-slate-400' : 'text-red-600'}`} />
                          <span>{member.name}</span>
                          <span className="text-[10px] font-normal text-slate-500">
                            ({member.bengkung ? cleanBengkungTitle(member.bengkung) : 'Pesilat'})
                          </span>
                          {isAlreadyAdded && (
                            <span className="text-[10px] text-emerald-600 font-bold ml-1">✓ Dipilih</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Guardian Info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 p-4 bg-slate-50/70 rounded-2xl border border-slate-100">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Nama Ibu Bapa / Waris <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Contoh: Ahmad bin Ismail"
                    value={parentName}
                    onChange={(e) => setParentName(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    No. WhatsApp / Telefon Waris <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="tel"
                    placeholder="Contoh: 012-3456789"
                    value={parentPhone}
                    onChange={(e) => setParentPhone(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Gelanggang Latihan <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={gelanggang}
                    onChange={(e) => setGelanggang(e.target.value as Gelanggang)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-semibold text-slate-900 focus:ring-2 focus:ring-red-500 focus:outline-none shadow-2xs"
                  >
                    {availableGelanggangOptions.map(g => (
                      <option key={g.id || g.name} value={g.name}>
                        Gelanggang {g.name} {g.location ? `(${g.location.split(',')[0]})` : ''}
                      </option>
                    ))}
                  </select>
                  <span className="text-[10px] text-slate-500 mt-1 block">
                    Bayaran yuran ujian disalurkan terus ke kaedah pembayaran gelanggang ini.
                  </span>
                </div>
              </div>

              {/* List of Candidates */}
              <div className="space-y-4">
                {candidates.map((cand, idx) => {
                  const availableRanks = getAvailableTargetRanks(cand.currentBengkung);

                  return (
                    <div
                      key={cand.id}
                      className="p-5 sm:p-6 rounded-3xl border border-slate-200 bg-white hover:border-slate-300 shadow-xs hover:shadow-sm transition-all relative space-y-5"
                    >
                      {/* Top Header Row of Candidate Card */}
                      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-2.5">
                          <span className="w-7 h-7 rounded-lg bg-slate-900 text-white text-xs font-black flex items-center justify-center shrink-0">
                            {idx + 1}
                          </span>
                          <h3 className="text-sm sm:text-base font-extrabold text-slate-900">
                            Calon {idx + 1}: {cand.name ? cand.name : 'Pesilat Baharu'}
                          </h3>
                        </div>

                        {candidates.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveCandidate(idx)}
                            className="text-slate-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition cursor-pointer"
                            title="Padam Calon Ini"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Main Form Fields: 2 Spacious Columns */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                        {/* Lajur 1: Nama Penuh Calon (Unified combobox) */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="block text-xs font-bold text-slate-700">
                              Nama Calon Pesilat <span className="text-red-600">*</span>
                            </label>
                            {familyMembers.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setActiveSearchIndex(activeSearchIndex === idx ? null : idx)}
                                className="text-[11px] text-red-600 hover:text-red-700 font-bold flex items-center gap-1 cursor-pointer"
                              >
                                <Search className="w-3 h-3" />
                                <span>Pilih Profil Keluarga ({familyMembers.length})</span>
                              </button>
                            )}
                          </div>

                          <div className="relative">
                            <input
                              type="text"
                              placeholder={familyMembers.length > 0 ? "Taip nama calon atau pilih dari profil keluarga..." : "Taip nama penuh calon pesilat..."}
                              value={cand.name}
                              onFocus={() => {
                                if (familyMembers.length > 0 && !cand.name) {
                                  setActiveSearchIndex(idx);
                                }
                              }}
                              onChange={(e) => {
                                const val = e.target.value;
                                handleUpdateCandidate(idx, 'name', val);
                                if (val.trim()) {
                                  setActiveSearchIndex(idx);
                                }
                                if (cand.memberId) {
                                  const matched = familyMembers.find((m) => m.id === cand.memberId);
                                  if (matched && matched.name !== val) {
                                    handleUpdateCandidate(idx, 'memberId', undefined);
                                  }
                                }
                              }}
                              className="w-full pl-3.5 pr-9 py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm focus:ring-2 focus:ring-red-500 focus:outline-none bg-white font-medium shadow-2xs"
                            />

                            {familyMembers.length > 0 && (
                              <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => setActiveSearchIndex(activeSearchIndex === idx ? null : idx)}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1 rounded-md cursor-pointer"
                                title="Buka senarai profil keluarga"
                              >
                                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeSearchIndex === idx ? 'rotate-180 text-red-600' : ''}`} />
                              </button>
                            )}

                            {/* Floating Popover for Family Members */}
                            {activeSearchIndex === idx && familyMembers.length > 0 && (
                              <>
                                <div
                                  className="fixed inset-0 z-20"
                                  onClick={() => setActiveSearchIndex(null)}
                                />
                                <div className="absolute z-30 top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-xl p-2 space-y-1 max-h-60 overflow-y-auto">
                                  <div className="text-[11px] font-bold text-slate-500 px-2 py-1 flex items-center justify-between border-b border-slate-100">
                                    <span>Pilih Ahli Keluarga ASMM:</span>
                                    <button
                                      type="button"
                                      onClick={() => setActiveSearchIndex(null)}
                                      className="text-slate-400 hover:text-slate-600 text-xs cursor-pointer font-bold"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                  {familyMembers
                                    .filter((m) => !cand.name || m.name.toLowerCase().includes(cand.name.toLowerCase().trim()))
                                    .map((m) => {
                                      const isAlreadySelected = candidates.some((c, i) => i !== idx && c.memberId === m.id);
                                      return (
                                        <button
                                          key={m.id}
                                          type="button"
                                          disabled={isAlreadySelected}
                                          onClick={() => {
                                            handleSelectFamilyMemberForCandidate(idx, m);
                                            setActiveSearchIndex(null);
                                          }}
                                          className={`w-full text-left p-2 rounded-xl text-xs flex items-center justify-between transition ${isAlreadySelected
                                            ? 'opacity-40 bg-slate-50 cursor-not-allowed'
                                            : 'hover:bg-red-50 hover:text-red-700 cursor-pointer'
                                            }`}
                                        >
                                          <div>
                                            <span className="font-bold text-slate-800 block">{m.name}</span>
                                            <span className="text-[10px] text-slate-500">
                                              Bengkung: {m.bengkung ? cleanBengkungTitle(m.bengkung) : 'Pesilat Asas'} • IC: {m.icNo || 'Tiada'}
                                            </span>
                                          </div>
                                          <span className={`text-[10px] font-bold px-2 py-1 rounded-lg ${isAlreadySelected
                                            ? 'bg-slate-200 text-slate-500'
                                            : 'bg-red-50 text-red-600 border border-red-200'
                                            }`}>
                                            {isAlreadySelected ? 'Dipilih' : 'Pilih'}
                                          </span>
                                        </button>
                                      );
                                    })}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Lajur 2: No MyKid / Kad Pengenalan */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700">
                            No. MyKid / Kad Pengenalan
                          </label>
                          <input
                            type="text"
                            placeholder="Contoh: 120512-02-1234"
                            value={cand.icNo}
                            onChange={(e) => handleUpdateCandidate(idx, 'icNo', e.target.value)}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm focus:ring-2 focus:ring-red-500 focus:outline-none bg-white font-medium shadow-2xs"
                          />
                        </div>

                        {/* Lajur 3: Tahap Bengkung Semasa */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700">
                            Tahap Bengkung Semasa
                          </label>
                          <select
                            value={cand.currentBengkung || ''}
                            onChange={(e) => handleUpdateCandidate(idx, 'currentBengkung', e.target.value)}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm focus:ring-2 focus:ring-red-500 focus:outline-none bg-slate-50 font-medium text-slate-800 shadow-2xs"
                          >
                            <option value="">Pesilat Baharu (Belum Berbengkung)</option>
                            {ASMM_BENGKUNG_RANKS.filter((r) => r.code !== 'Guru Utama').map((r) => (
                              <option key={r.code} value={r.code}>
                                {formatBengkungDetailedWithCula(r)}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Lajur 4: Tahap Bengkung Sasaran Ujian */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700">
                            Tahap Bengkung Sasaran Ujian <span className="text-red-600">*</span>
                          </label>
                          <select
                            value={cand.targetBengkung}
                            onChange={(e) => handleUpdateCandidate(idx, 'targetBengkung', e.target.value)}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm focus:ring-2 focus:ring-red-500 focus:outline-none bg-white font-semibold text-slate-900 shadow-2xs"
                          >
                            {availableRanks.length === 0 ? (
                              <option value="" disabled>
                                Peringkat Tertinggi (Tiada Ujian Lanjutan)
                              </option>
                            ) : (
                              availableRanks.map((r) => (
                                <option key={r.code} value={r.code}>
                                  {formatBengkungDetailedWithCula(r)} (RM {getUjianBengkungFeeForLevel(r.code, ujianFeesConfig || undefined)})
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                      </div>

                      {/* Card Footer Bar */}
                      <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                        <span className="flex items-center gap-1.5 font-semibold text-emerald-700">
                          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                          <span>Termasuk {pricingConfig.freeMealsPerPax} Pek Makanan Percuma & Tempat Duduk Waris Percuma di Dewan</span>
                        </span>
                        <span className="font-extrabold text-slate-900 sm:text-right">
                          Yuran Calon: <strong className="text-red-600">RM {cand.examFee}.00</strong>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleAddCandidate}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition cursor-pointer"
                >
                  <Plus className="w-4 h-4 text-slate-600" />
                  <span>+ Tambah Calon Seterusnya</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 5. SECTION: TEMPAHAN MAKANAN TAMBAHAN (ADD-ON KELUARGA) */}
        <div className="mb-10">
          <div className="mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
              <Utensils className="w-5 h-5 text-amber-500" />
              <span>Tempahan Makanan Tambahan (Pilihan Tambahan Keluarga)</span>
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              Setiap calon dibekalkan <strong>2 pek makanan percuma</strong> dan tempat duduk waris di dewan adalah <strong>percuma (tiada jualan tempat duduk)</strong>. Sekiranya anda memerlukan pek makanan tambahan untuk ahli keluarga lain, anda boleh menambahnya di sini:
            </p>
          </div>

          <div className="max-w-xl mx-auto mb-6">
            {/* Add-on Meals Counter */}
            <div className="bg-white rounded-3xl p-6 border-2 border-slate-200 hover:border-amber-400 transition-all shadow-xs relative flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                      <Utensils className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900">Pek Makanan Tambahan</h3>
                      <span className="text-xs text-slate-500">Pek katering berbungkus berkualiti</span>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-amber-50 text-amber-800 text-xs font-extrabold rounded-full border border-amber-200">
                    RM {pricingConfig.mealPrice} / pek
                  </span>
                </div>

                <div className="my-5 p-3.5 bg-slate-50 rounded-2xl flex items-baseline justify-between">
                  <div>
                    <span className="text-xs text-slate-500 block">Kadar Pek Tambahan:</span>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <span className="text-2xl font-black text-slate-900">
                        RM {pricingConfig.mealPrice}
                      </span>
                      <span className="text-xs text-slate-500 font-medium">/ pax</span>
                    </div>
                  </div>
                  <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                    2 Pek Calon Percuma Sudah Disediakan
                  </span>
                </div>
              </div>

              {/* Counter Buttons */}
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-slate-800 block">Pek Makanan Tambahan:</span>
                  <span className="text-[11px] text-slate-500">
                    Selain {totalCandidates > 0 ? `${freeFamilyMeals} pek percuma` : `kuota percuma (${pricingConfig.freeMealsPerPax} pek/calon)`}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setExtraMeals(Math.max(0, extraMeals - 1))}
                    disabled={extraMeals === 0}
                    className="w-9 h-9 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-white flex items-center justify-center text-slate-800 transition shadow-xs cursor-pointer"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center text-lg font-black text-slate-900">
                    {extraMeals}
                  </span>
                  <button
                    type="button"
                    onClick={() => setExtraMeals(extraMeals + 1)}
                    className="w-9 h-9 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 flex items-center justify-center text-slate-800 transition shadow-xs cursor-pointer"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 6. MAKLUMAT TEMPAT DUDUK & PEMBAYARAN GELANGGANG */}
        <div className="mb-10 p-5 sm:p-6 rounded-3xl bg-blue-50/80 border-2 border-blue-200">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="space-y-1.5">
              <h3 className="text-base font-black text-blue-950 uppercase tracking-tight">
                Tempat Duduk Waris Percuma & Urusan Bayaran Terus Ke Gelanggang
              </h3>
              <p className="text-xs sm:text-sm text-blue-900 leading-relaxed font-semibold">
                Tiada sebarang penjualan kerusi dewan — tempat duduk untuk waris dan keluarga calon disediakan secara percuma di ruang dewan bagi memberi sokongan kepada pesilat.
              </p>
              <p className="text-xs text-blue-800 leading-relaxed">
                Semua bayaran yuran ujian bengkung dan tempahan makanan tambahan disalurkan terus kepada akaun pembayaran <strong>Gelanggang {gelanggang}</strong> bagi memudahkan urusan pentadbiran dan rekod kehadiran bersama Jurulatih Gelanggang.
              </p>
            </div>
          </div>
        </div>

        {/* 7. COMPLETE FAMILY BREAKDOWN & PAYMENT SUMMARY (PECAHAN PENUH KELUARGA) */}
        <div className="mb-10 bg-white rounded-3xl border-2 border-slate-900 p-6 sm:p-8 shadow-xl">
          <div className="border-b border-slate-200 pb-4 mb-6 flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-red-600 uppercase tracking-wider block">
                Penyata & Ringkasan Bayaran
              </span>
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 mt-0.5">
                Pecahan Keseluruhan Keluarga
              </h2>
            </div>
            <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-xs font-bold">
              {totalCandidates > 0 ? `${totalCandidates} Calon Terlibat` : 'Keluarga ASMM'}
            </span>
          </div>

          {/* Itemized Table Breakdown */}
          <div className="space-y-3 mb-6">
            {/* Candidates exam fee rows */}
            {candidates.map((c, i) => (
              <div key={c.id} className="flex justify-between items-center py-2 border-b border-slate-100 text-xs sm:text-sm gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="font-bold text-slate-900 truncate block">{c.name || `Calon ${i + 1}`}</span>
                    <span className="text-slate-500 text-[11px] sm:text-xs font-medium">({c.targetBengkung})</span>
                  </div>
                </div>
                <span className="font-mono font-bold text-slate-900 shrink-0">RM {c.examFee}.00</span>
              </div>
            ))}

            {/* Free Family Package (0 RM) */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between py-2.5 px-3.5 bg-emerald-50 rounded-xl border border-emerald-200 text-xs sm:text-sm text-emerald-900 font-semibold gap-1.5 sm:gap-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="leading-snug">
                  {totalCandidates > 0
                    ? `Pakej Percuma Waris (${freeFamilyMeals} Pek Makanan Percuma + Tempat Duduk Percuma di Dewan)`
                    : `Pakej Percuma Waris (${pricingConfig.freeMealsPerPax} Pek Makanan Percuma Setiap Calon + Tempat Duduk Percuma)`
                  }
                </span>
              </div>
              <span className="font-black text-emerald-700 uppercase tracking-wider text-[11px] sm:text-xs shrink-0 sm:text-right">PERCUMA (RM 0)</span>
            </div>

            {/* Extra Meals Add-on */}
            {extraMeals > 0 && (
              <div className="flex justify-between items-center py-2 border-b border-slate-100 text-xs sm:text-sm gap-2">
                <div className="min-w-0 flex-1">
                  <span className="font-bold text-slate-900 block truncate">{extraMeals}x Pek Makanan Katering Tambahan</span>
                  <span className="text-slate-500 text-[11px] sm:text-xs font-medium">(@ RM {pricingConfig.mealPrice}/pax)</span>
                </div>
                <span className="font-mono font-bold text-slate-900 shrink-0">RM {totalExtraMealCost}.00</span>
              </div>
            )}
          </div>

          {/* Grand Total Callout Box */}
          <div className="p-4 sm:p-5 bg-slate-900 text-white rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-6 text-center sm:text-left">
            <div>
              <span className="text-[11px] sm:text-xs text-slate-400 font-bold uppercase tracking-wider block">
                Jumlah Keseluruhan Perlu Dibayar
              </span>
              <span className="text-[11px] sm:text-xs text-slate-300">
                Merangkumi yuran ujian {totalCandidates > 0 ? `${totalCandidates} calon` : 'calon'} (termasuk {freeFamilyMeals} pek makanan percuma & tempat duduk dewan percuma){extraMeals > 0 ? ` + ${extraMeals} pek makanan tambahan` : ''}
              </span>
            </div>
            <div className="sm:text-right">
              <span className="text-3xl sm:text-4xl font-black text-white block">
                RM {grandTotalWholeFamily}.00
              </span>
            </div>
          </div>

          {/* GELANGGANG PAYMENT SELECTION */}
          <div className="mb-6 p-4 bg-slate-50 rounded-2xl border border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mb-3">
              <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <Building className="w-4 h-4 text-red-600" />
                <span>Pilih Gelanggang Calon Anda:</span>
              </span>
              <span className="text-[11px] text-slate-500">
                Maklumat bayaran & DuitNow QR dipaparkan mengikut gelanggang dipilih
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {availableGelanggangOptions.map((g) => {
                const isSelected = gelanggang === g.name;
                return (
                  <button
                    key={g.id || g.name}
                    type="button"
                    onClick={() => setGelanggang(g.name as Gelanggang)}
                    className={`py-2.5 px-3.5 rounded-xl text-xs font-bold transition flex items-center justify-between cursor-pointer border ${
                      isSelected
                        ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                    }`}
                  >
                    <span>{g.name}</span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* PAYMENT METHOD SELECTION (FPX vs QR) */}
          <div className="border-t border-slate-200 pt-5 sm:pt-6">
            {isFpxEnabled && isQrEnabled && (
              <div className="mb-5">
                <span className="text-xs font-bold text-slate-700 block mb-2">
                  Pilih Kaedah Pembayaran Pilihan Anda:
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedPayMode('fpx')}
                    className={`py-3 px-3.5 rounded-2xl border-2 transition-all flex items-center justify-center gap-2 cursor-pointer font-bold text-xs sm:text-sm ${selectedPayMode === 'fpx'
                      ? 'border-red-600 bg-red-50 text-red-700 shadow-xs'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                  >
                    <CreditCard className="w-4 h-4 text-red-600 shrink-0" />
                    <span>Perbankan FPX (ToyyibPay)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedPayMode('qr')}
                    className={`py-3 px-3.5 rounded-2xl border-2 transition-all flex items-center justify-center gap-2 cursor-pointer font-bold text-xs sm:text-sm ${selectedPayMode === 'qr'
                      ? 'border-amber-500 bg-amber-50 text-amber-900 shadow-xs'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                  >
                    <QrCode className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>DuitNow QR Pay & Pindahan</span>
                  </button>
                </div>
              </div>
            )}

            {/* OPTION A: FPX ONLINE PAYMENT */}
            {selectedPayMode === 'fpx' && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-xs text-slate-600 leading-relaxed flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-slate-900 font-bold mb-0.5">
                      Pembayaran Dalam Talian FPX Selamat (ToyyibPay)
                    </strong>
                    Invois rasmi ASMM akan dijana secara automatik. Anda boleh membayar terus menggunakan Maybank2u, CIMB Clicks, {persatuanSettings.bankName || 'Bank Islam'}, RHB, atau bank pilihan anda dengan pengesahan resit masa nyata.
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={handleCreateFamilyInvoiceAndPay}
                    disabled={isProcessingPayment}
                    className="py-4 px-6 bg-red-600 hover:bg-red-700 text-white font-extrabold text-sm sm:text-base rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2.5 cursor-pointer disabled:opacity-50"
                  >
                    <CreditCard className="w-5 h-5" />
                    <span>{isProcessingPayment ? 'Menjana Invois...' : `Bayar Sekarang RM ${grandTotalWholeFamily} (FPX)`}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSendWhatsApp(false)}
                    className="py-4 px-6 bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-sm sm:text-base rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2.5 cursor-pointer"
                  >
                    <MessageCircle className="w-5 h-5 text-emerald-400" />
                    <span>Hantar Butiran ke WhatsApp Gelanggang</span>
                  </button>
                </div>
              </div>
            )}

            {/* OPTION B: DUITNOW QR PAY & BANK TRANSFER GELANGGANG */}
            {selectedPayMode === 'qr' && (
              <div className="space-y-4">
                {/* 1. Official Gelanggang Bank & DuitNow QR Card */}
                <div className="p-4 sm:p-5 bg-amber-50/70 rounded-2xl sm:rounded-3xl border-2 border-amber-200 space-y-4">
                  <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4 sm:gap-5">
                    <div className="space-y-2 text-center sm:text-left flex-1 min-w-0">
                      <div className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-amber-200 text-amber-900 text-xs font-black uppercase tracking-wider">
                          <QrCode className="w-3.5 h-3.5" />
                          <span>DuitNow QR Gelanggang</span>
                        </span>
                        <span className="px-2.5 py-0.5 rounded-full bg-slate-900 text-white text-[10px] font-black uppercase tracking-wider">
                          {gelanggang}
                        </span>
                      </div>

                      <h3 className="text-base sm:text-lg font-black text-slate-900">
                        {targetBankName}
                      </h3>

                      <p className="text-xs text-slate-600 font-medium leading-snug">
                        Pemegang Akaun: <strong className="text-slate-900">{targetAccountHolder}</strong>
                      </p>

                      <div className="flex items-center justify-center sm:justify-start gap-2 pt-1 flex-wrap">
                        <span className="font-mono text-sm sm:text-lg font-black text-slate-900 bg-white px-3 py-1 rounded-xl border border-amber-300 shadow-2xs break-all">
                          {targetAccountNo}
                        </span>
                        <button
                          type="button"
                          onClick={handleCopyAccountNo}
                          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl transition cursor-pointer flex items-center gap-1 shadow-2xs active:scale-95"
                        >
                          {isAccountCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          <span>{isAccountCopied ? 'Disalin' : 'Salin'}</span>
                        </button>
                      </div>

                      <div className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 pt-1 text-[11px] text-amber-900 flex-wrap">
                        <span>Rujukan Bayaran: <strong className="font-mono text-xs sm:text-sm font-black text-slate-900 bg-white px-2.5 py-1 rounded-xl border border-amber-300 shadow-2xs tracking-wide">{candidateRefText}</strong></span>
                        <button
                          type="button"
                          onClick={handleCopyRefText}
                          className="px-2.5 py-1 bg-white hover:bg-amber-100 border border-amber-300 text-amber-900 text-[10px] font-bold rounded-lg transition cursor-pointer flex items-center gap-1 active:scale-95 shadow-2xs"
                          title="Salin Rujukan Bayaran"
                        >
                          {isRefNotesCopied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-amber-700" />}
                          <span>{isRefNotesCopied ? 'Disalin' : 'Salin'}</span>
                        </button>
                      </div>

                      <p className="text-[11px] text-amber-900/90 font-medium leading-relaxed pt-0.5">
                        Sila buat bayaran terus ke akaun {targetBankName} Gelanggang {gelanggang} dan masukkan rujukan "{candidateRefText}" semasa pindahan perbankan.
                      </p>
                    </div>

                    {/* QR Code Container */}
                    <div className="w-36 sm:w-44 bg-white p-2.5 rounded-2xl border-2 border-amber-300 shadow-sm flex flex-col items-center justify-center shrink-0 mx-auto sm:mx-0">
                      {targetQrImage && targetQrImage.trim().startsWith('<svg') ? (
                        <div
                          className="w-28 h-28 sm:w-32 sm:h-32 aspect-square flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
                          dangerouslySetInnerHTML={{ __html: targetQrImage }}
                        />
                      ) : (
                        <img
                          src={targetQrImage || '/badges/qr-persatuan-asmm.png'}
                          alt={`DuitNow QR ${gelanggang}`}
                          className="w-28 h-28 sm:w-32 sm:h-32 object-contain rounded-lg"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (target.src.indexOf('/badges/qr-persatuan-asmm.png') === -1) {
                              target.src = '/badges/qr-persatuan-asmm.png';
                            }
                          }}
                        />
                      )}
                      <span className="text-[10px] font-black text-slate-900 uppercase tracking-wider text-center mt-1.5 truncate max-w-full">
                        {gelanggang}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 2. Official Receipt Upload & Verification Form */}
                {isReceiptSubmitted && submittedInvoice ? (
                  /* Success Confirmation State */
                  <div className="p-5 sm:p-6 bg-emerald-50/90 border-2 border-emerald-300 rounded-2xl sm:rounded-3xl text-center space-y-4 shadow-sm animate-in fade-in duration-300">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto ring-4 ring-emerald-200/60 shadow-xs">
                      <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8" />
                    </div>
                    <div>
                      <span className="inline-block px-3 py-1 rounded-full bg-emerald-200/80 text-emerald-900 text-[10px] sm:text-xs font-black uppercase tracking-wider mb-1.5">
                        Resit Bayaran Diterima
                      </span>
                      <h3 className="text-base sm:text-xl font-black text-slate-900">
                        Pendaftaran Ujian Bengkung Berjaya Dihantar!
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto mt-1 leading-relaxed">
                        Fail resit telah disimpan dalam arkib rasmi Google Drive JSON Ahli. Status pendaftaran kini <strong>Menunggu Pengesahan</strong> bendahari ASMM.
                      </p>
                    </div>

                    {/* Verification Details Box */}
                    <div className="bg-white p-3.5 sm:p-4 rounded-2xl border border-emerald-200/80 text-left text-xs space-y-2 max-w-lg mx-auto">
                      <div className="flex justify-between items-center py-1 border-b border-slate-100">
                        <span className="text-slate-500 font-medium">No. Invois:</span>
                        <span className="font-mono font-black text-slate-900">{submittedInvoice.invoiceNo}</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-slate-100">
                        <span className="text-slate-500 font-medium">No. Rujukan:</span>
                        <span className="font-mono font-black text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-200">
                          {submittedInvoice.transactionRef || receiptRefId || candidateRefText}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-slate-100">
                        <span className="text-slate-500 font-medium">Jumlah Bayaran:</span>
                        <span className="font-mono font-black text-slate-900 text-sm">RM {submittedInvoice.amount}.00</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-slate-100">
                        <span className="text-slate-500 font-medium">Tarikh & Masa:</span>
                        <span className="font-mono font-bold text-slate-700">{submittedInvoice.paidAt || `${paidDate} ${paidTime}`}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-slate-500 font-medium">Nama Fail Resit:</span>
                        <span className="font-mono text-[11px] text-slate-600 truncate max-w-[180px] sm:max-w-[240px]" title={submittedInvoice.receiptFileName}>
                          {submittedInvoice.receiptFileName || 'Slip_Resit_Bayaran'}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 pt-2 max-w-lg mx-auto">
                      <button
                        type="button"
                        onClick={handleSendSubmittedReceiptWhatsApp}
                        className="py-3 sm:py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs sm:text-sm rounded-2xl shadow-md hover:shadow-lg transition flex items-center justify-center gap-2 cursor-pointer active:scale-95"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>Hantar ke WhatsApp Jurulatih</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setIsReceiptSubmitted(false);
                          setUploadedReceiptFile(null);
                          setReceiptFilePreview(null);
                        }}
                        className="py-3 sm:py-3.5 px-4 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 font-bold text-xs sm:text-sm rounded-2xl transition flex items-center justify-center gap-2 cursor-pointer"
                      >
                        <RefreshCw className="w-4 h-4 text-slate-500" />
                        <span>Muat Naik Resit Baharu</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Standard Upload Card */
                  <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-2xs space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                      <div className="flex items-center gap-2.5">
                        <span className="p-2 bg-red-50 text-red-600 rounded-xl border border-red-100 shrink-0">
                          <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                        </span>
                        <div>
                          <h4 className="text-xs sm:text-base font-black text-slate-900">
                            Muat Naik Resit Bayaran (PDF / Imej)
                          </h4>
                          <p className="text-[10px] sm:text-[11px] text-slate-500">
                            Sila muat naik slip transaksi (.pdf, .png, .jpg, .jpeg, .webp maks 10MB)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Dropzone */}
                    <label className={`flex flex-col items-center justify-center p-4 sm:p-6 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${uploadedReceiptFile
                      ? 'border-emerald-500 bg-emerald-50/20'
                      : 'border-slate-300 hover:border-red-500 hover:bg-red-50/10 bg-slate-50/50'
                      }`}>
                      <div className="p-2.5 sm:p-3 bg-red-50 text-red-600 rounded-2xl mb-2">
                        <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      <span className="text-xs font-bold text-slate-800 text-center max-w-xs break-all">
                        {uploadedReceiptFile ? uploadedReceiptFile.name : 'Pilih atau Tarik Fail Resit (PDF / Imej)'}
                      </span>
                      <span className="text-[10px] text-slate-400 mt-1 text-center">
                        Format fail: Dokumen PDF (.pdf) atau fail imej (.png, .jpg, .jpeg, .webp). Maksimum 10MB.
                      </span>
                      <input
                        type="file"
                        accept="application/pdf,image/*,.pdf,.png,.jpg,.jpeg,.webp"
                        onChange={handleReceiptFileChange}
                        className="hidden"
                      />
                    </label>

                    {/* Receipt Image Preview */}
                    {receiptFilePreview && (
                      <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200 text-center">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-2">Pratonton Resit Bayaran</span>
                        <img
                          src={receiptFilePreview}
                          alt="Pratonton Resit"
                          className="max-h-48 max-w-full mx-auto rounded-xl object-contain border border-slate-200 shadow-2xs"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Scanning PDF / Image Progress Banner */}
                    {isScanningPdf && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-2xl flex items-center gap-2.5 animate-pulse">
                        <Loader2 className="w-4 h-4 text-amber-600 animate-spin shrink-0" />
                        <div className="text-left min-w-0">
                          <p className="text-xs font-black text-amber-950 flex items-center gap-1.5">
                            <ScanText className="w-3.5 h-3.5 text-amber-700" />
                            <span>Mengimbas Butiran Resit Secara Pintar...</span>
                          </p>
                          <p className="text-[11px] text-amber-800 truncate font-medium">
                            {scanProgress || 'Mencari nombor rujukan & tarikh transaksi...'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* File Details & Audit Form Fields */}
                    {uploadedReceiptFile && (
                      <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="p-1.5 bg-emerald-100 text-emerald-800 rounded-lg shrink-0">
                              <FileText className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 text-left">
                              <span className="text-xs font-bold text-slate-900 truncate block">
                                {uploadedReceiptFile.name}
                              </span>
                              <span className="text-[10px] text-slate-500 font-mono">
                                {(uploadedReceiptFile.size / 1024).toFixed(1)} KB • {uploadedReceiptFile.type.includes('pdf') ? 'Dokumen PDF' : 'Fail Imej'}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setUploadedReceiptFile(null);
                              setReceiptFilePreview(null);
                            }}
                            className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition cursor-pointer"
                            title="Padam Fail"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Audit Inputs */}
                        <div className="pt-2 border-t border-slate-200/80 space-y-2.5 text-left">
                          <div>
                            <label className="block text-[11px] font-bold text-slate-700 mb-0.5">
                              No. Rujukan Transaksi (Reference ID):
                            </label>
                            <input
                              type="text"
                              value={receiptRefId}
                              onChange={(e) => setReceiptRefId(e.target.value)}
                              placeholder={`cth: ${candidateRefText} / 045453508Q`}
                              className="w-full px-3 py-2 text-xs font-mono font-black text-slate-900 bg-white border border-slate-300 rounded-xl focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none"
                            />
                            <span className="text-[9px] text-slate-400">
                              Disahkan oleh bendahari ASMM berdasarkan rekod pindahan bank.
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[11px] font-bold text-slate-700 mb-0.5">
                                Tarikh Pembayaran:
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                                  <Calendar className="w-3.5 h-3.5" />
                                </div>
                                <input
                                  type="date"
                                  value={paidDate}
                                  onChange={(e) => setPaidDate(e.target.value)}
                                  className="w-full pl-8 pr-2 py-2 text-xs font-bold text-slate-900 bg-white border border-slate-300 rounded-xl focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none cursor-pointer"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-[11px] font-bold text-slate-700 mb-0.5">
                                Masa Pembayaran:
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                                  <Clock className="w-3.5 h-3.5" />
                                </div>
                                <input
                                  type="time"
                                  step="1"
                                  value={paidTime}
                                  onChange={(e) => setPaidTime(e.target.value)}
                                  className="w-full pl-8 pr-2 py-2 text-xs font-bold text-slate-900 bg-white border border-slate-300 rounded-xl focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none cursor-pointer"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Primary Upload Action Button */}
                    <button
                      type="button"
                      onClick={handleUploadAndSubmitReceipt}
                      disabled={isUploadingReceipt || !uploadedReceiptFile}
                      className="w-full py-3.5 sm:py-4 px-6 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-black text-xs sm:text-sm rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed active:scale-98"
                    >
                      {isUploadingReceipt ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Memuat Naik Resit ke Google Drive...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          <span>Muat Naik & Hantar Pengesahan Resit (RM {grandTotalWholeFamily}.00)</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Secondary Action: Switch to FPX if enabled */}
                {isFpxEnabled && (
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={handleCreateFamilyInvoiceAndPay}
                      disabled={isProcessingPayment}
                      className="w-full py-3.5 sm:py-4 px-5 bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs sm:text-sm rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      <CreditCard className="w-4 h-4 text-amber-400" />
                      <span>Tukar ke Bayaran FPX (ToyyibPay)</span>
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* 8. SECTION: CONTACT PENGURUSAN GELANGGANG */}
        <div className="mb-10">
          <div className="mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              <span>Pengurusan Gelanggang Untuk Pengesahan Segera</span>
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              Sila hubungi terus pihak pengurusan gelanggang anda untuk pertanyaan berkaitan ujian bengkung atau pengesahan kehadiran
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Gelanggang Pusaka Warisan */}
            <div
              onClick={() => setGelanggang('Pusaka Warisan')}
              className={`bg-white p-5 rounded-2xl border transition flex flex-col justify-between shadow-xs cursor-pointer ${gelanggang === 'Pusaka Warisan' ? 'border-red-500 ring-2 ring-red-100' : 'border-slate-200 hover:border-slate-300'}`}
            >
              <div>
                <div className="w-9 h-9 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mb-3">
                  <Building className="w-5 h-5" />
                </div>
                <div className="flex items-center justify-between">
                  <h3 className="font-extrabold text-slate-900 text-sm">Pusaka Warisan</h3>
                  {gelanggang === 'Pusaka Warisan' && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">Pilihan Semasa</span>
                  )}
                </div>
                <p className="text-xs font-bold text-slate-800 mt-1">Jurulatih Fikriz Zaki / Mohammad Hafiz</p>
                <p className="text-[11px] text-slate-500">Ketua Gelanggang Pusaka Warisan</p>
                <div className="mt-2 text-xs font-mono font-bold text-slate-800">
                  013-581 9201
                </div>
              </div>
              <a
                href="https://wa.me/60135819201?text=Assalamualaikum%20Jurulatih%20Pusaka%20Warisan%2C%20saya%20ingin%20membuat%20pengesahan%20Ujian%20Bengkung."
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="mt-4 w-full py-2.5 bg-slate-50 hover:bg-red-50 text-red-600 font-bold text-xs rounded-xl border border-slate-200 hover:border-red-200 transition text-center flex items-center justify-center gap-1.5"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>WhatsApp Pusaka Warisan</span>
              </a>
            </div>

            {/* Gelanggang Wadi AnNour */}
            <div
              onClick={() => setGelanggang('Wadi AnNour')}
              className={`bg-white p-5 rounded-2xl border transition flex flex-col justify-between shadow-xs cursor-pointer ${gelanggang === 'Wadi AnNour' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}
            >
              <div>
                <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                  <Building className="w-5 h-5" />
                </div>
                <div className="flex items-center justify-between">
                  <h3 className="font-extrabold text-slate-900 text-sm">Wadi AnNour</h3>
                  {gelanggang === 'Wadi AnNour' && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full">Pilihan Semasa</span>
                  )}
                </div>
                <p className="text-xs font-bold text-slate-800 mt-1">Jurulatih Mohd Fauzi Bin Abd Aziz</p>
                <p className="text-[11px] text-slate-500">Ketua Gelanggang Wadi AnNour</p>
                <div className="mt-2 text-xs font-mono font-bold text-slate-800">
                  012-447 5157
                </div>
              </div>
              <a
                href="https://wa.me/60124475157?text=Assalamualaikum%20Jurulatih%20Mohd%20Fauzi%2C%20saya%20ingin%20membuat%20pengesahan%20Ujian%20Bengkung."
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="mt-4 w-full py-2.5 bg-slate-50 hover:bg-blue-50 text-blue-600 font-bold text-xs rounded-xl border border-slate-200 hover:border-blue-200 transition text-center flex items-center justify-center gap-1.5"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>WhatsApp Wadi AnNour</span>
              </a>
            </div>

            {/* Gelanggang Paya Besar */}
            <div
              onClick={() => setGelanggang('Paya Besar')}
              className={`bg-white p-5 rounded-2xl border transition flex flex-col justify-between shadow-xs cursor-pointer ${gelanggang === 'Paya Besar' ? 'border-amber-500 ring-2 ring-amber-100' : 'border-slate-200 hover:border-slate-300'}`}
            >
              <div>
                <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-3">
                  <Building className="w-5 h-5" />
                </div>
                <div className="flex items-center justify-between">
                  <h3 className="font-extrabold text-slate-900 text-sm">Paya Besar</h3>
                  {gelanggang === 'Paya Besar' && (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded-full">Pilihan Semasa</span>
                  )}
                </div>
                <p className="text-xs font-bold text-slate-800 mt-1">Jurulatih Mohamad Shakir Fahmi</p>
                <p className="text-[11px] text-slate-500">Ketua Gelanggang Paya Besar</p>
                <div className="mt-2 text-xs font-mono font-bold text-slate-800">
                  011-2819 4012
                </div>
              </div>
              <a
                href="https://wa.me/601128194012?text=Assalamualaikum%20Jurulatih%20Shakir%20Fahmi%2C%20saya%20ingin%20membuat%20pengesahan%20Ujian%20Bengkung."
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="mt-4 w-full py-2.5 bg-slate-50 hover:bg-amber-50 text-amber-700 font-bold text-xs rounded-xl border border-slate-200 hover:border-amber-200 transition text-center flex items-center justify-center gap-1.5"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>WhatsApp Paya Besar</span>
              </a>
            </div>
          </div>
        </div>

        {/* 9. FAQ & SOALAN LAZIM */}
        <div className="p-6 sm:p-8 rounded-3xl bg-slate-50 border border-slate-200 text-left">
          <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-slate-600" />
            <span>Soalan Lazim & Panduan Kehadiran Dewan</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Berapa kuota pek makanan percuma dan adakah tempat duduk waris dikenakan bayaran?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Setiap calon yang mendaftar menerima <strong>2 pek makanan percuma</strong> untuk waris/keluarga. Tempat duduk di ruang dewan disediakan secara <strong>percuma (tiada penjualan tempat duduk)</strong> bagi membolehkan ibu bapa dan keluarga hadir memberi sokongan penuh kepada pesilat.
              </p>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Bolehkah saya menempah makanan tambahan untuk ahli keluarga lain?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Boleh. Sekiranya keluarga anda memerlukan makanan melebihi 2 pek percuma, tempahan pek makanan katering tambahan boleh dibuat pada kadar standard <strong>RM {pricingConfig.mealPrice} setiap pek</strong> melalui borang di atas.
              </p>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Bagaimanakah kaedah pembayaran yuran ujian bengkung dibuat?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Pembayaran dibuat terus ke <strong>kaedah pembayaran Gelanggang masing-masing</strong> (Pusaka Warisan, Wadi AnNour, atau Paya Besar). Nombor akaun bank dan kod DuitNow QR gelanggang dipaparkan secara automatik mengikut gelanggang calon yang dipilih.
              </p>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Apakah rujukan yang perlu diletakkan semasa membuat pindahan DuitNow QR / Bank?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Sila masukkan rujukan rasmi seperti <strong>"{candidateRefText}"</strong> (gabungan kod UB2026 bersama nombor ID pesilat) pada resit pindahan perbankan anda bagi memudahkan pihak gelanggang menyemak dan mengesahkan rekod bayaran calon.
              </p>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Bagaimana makanan diedarkan semasa majlis?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Pek katering berbungkus berkualiti akan diedarkan di kaunter pendaftaran gelanggang semasa majlis mengikut senarai nama waris dan jumlah pek yang telah disahkan.
              </p>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Bagaimana jika keluarga saya mempunyai lebih daripada seorang calon?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Anda boleh klik butang <strong>+ Tambah Calon Seterusnya</strong> untuk mendaftar adik-beradik atau ahli keluarga lain dalam satu resit bayaran yang sama. Setiap calon akan menerima kuota 2 pek makanan percuma masing-masing.
              </p>
            </div>
          </div>
        </div>

        {/* Closing Footnote */}
        <div className="mt-8 text-center text-xs text-slate-500">
          <p className="font-semibold text-slate-700">Sekian, terima kasih.</p>
          <p className="mt-1">Jawatankuasa Pengurusan Ujian Bengkung • Persatuan Seni Silat Awang Sulung Merah Muda</p>
        </div>

      </div>
    </div>
  );
};

export default UjianBengkungPage;
